import i18n from 'i18next';
import _ from 'lodash';
import moment from 'moment';

import { PAY_AT_TABLE_ORDER_UPDATE_CODE } from '@/legacy/ui-common/server-errors';
import OrderTimeSlots from './class.OrderTimeSlots';
import OrderConfirmabilityStatus from './class.OrderConfirmabilityStatus';
import {
    isTablePlatform,
    isKioskPlatform,
    isPayAtTablePlatform,
    PLATFORMS,
} from './app';
import { headerCase } from 'change-case';
const { app, Customer, headoffice } = Mobi2Go;
Mobi2Go._class = Mobi2Go._class || {};

const { Price } = Mobi2Go._class.Common;
const { Discount } = Mobi2Go._class.Common;

let instance_count = 0;

const isUpsellPreconfirmationEnabled = headoffice.upsell_preconfirmation_enabled;

/**-----------------------------------------------------------------------------
 * Order Module 2000 (Champion edition)
 * Represents an instance of an order
 */
Mobi2Go._class.Order = function(id) {
    id = id || null;

    let is_ready = false;

    /*--------------------------------------------------------------------------
     * Events
     */
    const events = Mobi2Go.app.mixinEvents(
        `Order${instance_count++ ? instance_count : ''}`
    );
    const { bind } = events;
    const { trigger } = events;
    const { unbind } = events;
    const { once } = events;

    /*--------------------------------------------------------------------------
     * Order properties
     */
    const server_time_slots = Mobi2Go.app.feature_flags?.server_time_slots;
    const time_slots = new OrderTimeSlots(events);
    const confirmability_status = new OrderConfirmabilityStatus(events);
    let is_test = false;
    let customer = null; // _class.Customer
    let address = null; // _class.CustomerAddress
    let location = null; // _class.Location
    let method = null;
    let table_id = null;
    let is_table_ordering_enabled = false;
    const products = Mobi2Go._class.OrderProducts(events);
    let menu = null;
    let available_menus = [];
    let product_suggestions = [];
    let product_suggestions_override = [];
    const payment = {
        total: new Price(0.0),
        method: null,
        status: 'method required',
        card_token_id: null,
        card_token_saving_enabled: false,
        available_card_tokens: [],
        available_processors: [],
        processor: null,
    };
    let wait_time = null;
    let expected_at = null;
    let earliest_expected_at = null;
    let comment = '';
    let metadata = {};
    let custom_html = {};
    const voucher = {
        code: '',
        voucher_applied: null,
        error_message: '',
        will_unapply_on_confirmation: false,
    };
    let loyalty = {};
    let reward = null;

    const delivery_minimum_total = new Price(0.0);
    const surcharge = new Price(0.0);
    const tip = new Price(0.0);
    const credit = new Price(0.0);

    // Rebates
    let rebates = [];
    let rebates_applied_total = new Price(0.0);

    let tax_totals = [];
    let latitude = null;
    let longitude = null;
    let language = null;

    const delivery_fee = new Price(0.0);
    const delivery_fee_discount = new Discount(0.0);
    let discounts = [];
    const discounts_total = new Discount(0.0);
    const total = new Price(0.0);

    let is_setting = false;
    let is_resetting = false;
    const method_labels = {
        pickup: i18n.t('order.method:pickup'),
        eatin: i18n.t('order.method:eatin'),
        delivery: i18n.t('order.method:delivery'),
    };
    let self = {};

    let menu_level_id = null;
    let kiosk_id = null;
    let collect_id = null;

    let available_post_confirmation_statuses = [];
    let current_post_confirmation_status = null;

    const curbside_pickup_details = {
        colour: '',
        make: '',
        brand: '',
        plate_number: '',
    };
    let isCurbsidePickup = false;

    /*--------------------------------------------------------------------------
     * State for comparison
     */
    let available_methods = [];
    let available_payment_methods = []; // 'online', 'offline'
    let available_payment_options = []; // includes 'offline', 'online' and tokens
    let available_addresses = [];
    let available_delivery_areas = [];
    let available_dates = [];
    let available_times = [];
    let available_rewards = [];
    let menu_restrictions = {};
    let menu_tier_id = null;
    let minutes_to_closing = null;
    let expected_in_string = null;
    let was_asap_time_before_earliest_expected_at_adjustment = null;
    let was_asap_time_before_wait_time_adjustment = null;

    /**-------------------------------------------------------------------------
     * Padding to the 'minutes to closing' value
     *
     * It is risky to allow customers to order right up to the store's
     * closing time because confirmation (payment + dispatching) may take
     * a few minutes. If confirmation takes too long it's possible that the
     * order's expected time may roll over to the next day.
     */
    const MINUTES_TO_CLOSING_BUFFER = 4;

    /*--------------------------------------------------------------------------
     * Set/Get
     */
    function get() {
        return {
            id,
            is_test,
            customer,
            address,
            location,
            method,
            expected_at,
            products: products.get(),
            payment,
            delivery_fee,
            comment,
            voucher,
            reward,
            credit,
            total,
            progress,
            latitude,
            longitude,
            platform: Mobi2Go.app.getPlatform(),
            build_request_id: Mobi2Go.app.build_request_id,
            language,
            menu_level_id,
            kiosk_id,
            table_id,
            collect_id,
            available_post_confirmation_statuses,
            current_post_confirmation_status,
            curbside_pickup_details,
        };
    }

    function set(properties) {
        collect_id = properties.collect_id;

        if (
            properties.progress !== undefined &&
            properties.progress.phase !== undefined &&
            properties.progress.phase === 'abandoned'
        ) {
            if (properties.progress.errors !== undefined) {
                setProgressErrors(properties.progress.errors);
            }
            setProgressPhase(properties.progress.phase);
            return self;
        }

        is_setting = true;

        if (properties.id !== undefined) {
            setId(properties.id);
        }

        if (properties.is_test !== undefined) {
            setIsTest(properties.is_test);
        }

        // Location
        if (properties.location !== undefined) {
            if (!properties.location) {
                setLocation(null);
            } else if (properties.location.id !== undefined) {
                setLocationById(properties.location.id);
            } else if (properties.location.name !== undefined) {
                setLocationByName(properties.location.name);
            }
        }

        // Customer
        if (properties.customer !== undefined) {
            // If the server responds with a null customer, this indicates
            // that the server believes that the customer is not logged in
            // Triggering 'updateCustomer' null will allow to the Customer
            // module to double check its logged-in state
            if (properties.customer === null && customer !== null) {
                trigger('updateCustomer', null);
            }

            // It should also update the current customer
            if (
                properties.customer !== null &&
                customer !== null &&
                properties.customer.id === customer.id
            ) {
                customer.set(properties.customer);
            }
        }

        // Delivery address
        if (properties.address !== undefined) {
            if (!properties.address) {
                setAddress(null);
            } else if (customer && customer.is_guest) {
                setAddressForGuest(properties.address);
            } else if (properties.address.id !== undefined) {
                setAddressById(properties.address.id);
            } else if (customer) {
                customer.createAddress(properties.address, setAddress);
            }
        }

        // Order method
        if (properties.method !== undefined) {
            setMethod(properties.method);
        }

        // Curbside Pickup Details (only when pickup is selected)
        if (
            properties.curbside_pickup_details &&
            properties.method === 'pickup'
        ) {
            setCurbsidePickupDetails(properties.curbside_pickup_details);
        }

        // Wait Time
        if (properties.wait_time !== undefined) {
            setWaitTime(properties.wait_time);
        }

        // Time Slots
        if (properties.time_slots !== undefined) {
            time_slots.set(properties.time_slots);
        }

        // Expected date
        if (properties.expected_at !== undefined) {
            setExpectedAt(properties.expected_at);
        }

        // Earliest Expected at
        if (properties.earliest_expected_at !== undefined) {
            setEarliestExpectedAt(properties.earliest_expected_at);
        }

        // Order comment
        if (properties.comment !== undefined) {
            setComment(properties.comment);
        }

        // Available menus
        if (properties.available_menus !== undefined) {
            setAvailableMenus(properties.available_menus);
        }

        // Menu
        if (properties.menu !== undefined) {
            setMenu(properties.menu);
        }

        // Products
        if (properties.products !== undefined) {
            products.set(properties.products);
        }

        // Delivery fee
        if (properties.delivery_fee !== undefined) {
            setDeliveryFee(properties.delivery_fee);
        }

        // Delivery fee discount
        if (properties.delivery_fee_discount !== undefined) {
            setDeliveryFeeDiscount(properties.delivery_fee_discount);
        }

        // Voucher
        if (properties.voucher !== undefined) {
            setVoucher(properties.voucher);
        }

        if (properties.metadata !== undefined) {
            setMetadata(properties.metadata);
        }

        if (properties.custom_html !== undefined) {
            setCustomHtml(properties.custom_html);
        }

        // Reward
        if (properties.reward !== undefined) {
            setReward(properties.reward);
        }

        // Product suggestions
        if (properties.product_suggestions !== undefined) {
            setProductSuggestions(properties.product_suggestions);
        }

        // Loyalty
        if (properties.loyalty !== undefined) {
            setLoyalty(properties.loyalty);
        }

        // Discounts
        if (properties.discounts !== undefined) {
            setDiscounts(properties.discounts);
        }

        // Discounts total
        if (properties.list_discounts_total !== undefined) {
            setDiscountsTotal(properties.list_discounts_total);
        }

        // Delivery Minimum
        if (properties.delivery_minimum_total !== undefined) {
            setDeliveryMinimumTotal(properties.delivery_minimum_total);
        }

        // Surcharge
        if (properties.surcharge !== undefined) {
            setSurcharge(properties.surcharge);
        }

        // Tip
        if (properties.tip !== undefined) {
            setTip(properties.tip, false); // Server will always return tax-inclusive amount
        }

        // Payment method
        if (properties.payment !== undefined) {
            setPayment(properties.payment);
        }

        // Dispatcher
        if (properties.dispatcher !== undefined) {
            setDispatcher(properties.dispatcher);
        }

        // Taxes
        if (properties.tax_totals !== undefined) {
            setTaxTotals(properties.tax_totals);
        }

        // Progress
        if (properties.progress !== undefined) {
            setProgress(properties.progress);
        }

        // Latitude
        if (properties.latitude !== undefined) {
            latitude = properties.latitude;
        }

        // Longitude
        if (properties.longitude !== undefined) {
            longitude = properties.longitude;
        }

        if (properties.language !== undefined) {
            setLanguage(properties.language);
        }

        is_setting = false;

        if (properties.total !== undefined) {
            setTotal(properties.total);
        } else {
            calculateTotal();
        }

        // Credit
        if (properties.credit !== undefined) {
            setCredit(properties.credit);
            automaticallyAdjustCredit(); // Temp - remove
        }

        // Rebates
        setRebates(properties.rebates !== undefined ? properties.rebates : []);

        if (properties.menu_level_id !== undefined) {
            setMenuLevelId(properties.menu_level_id);
        }

        if (properties.kiosk_id !== undefined) {
            setKioskId(properties.kiosk_id);
        }

        if (properties.table_id !== undefined) {
            setTableId(properties.table_id);
        }

        if (properties.available_post_confirmation_statuses !== undefined) {
            setAvailablePostConfirmationStatuses(
                properties.available_post_confirmation_statuses
            );
        }

        if (properties.post_confirmation_status !== undefined) {
            setCurrentPostConfirmationStatus(
                properties.post_confirmation_status
            );
        }

        return self;
    }

    function setId(new_id) {
        if (id !== new_id) {
            id = new_id;
            trigger('updateId', id);
        }
    }

    /**-------------------------------------------------------------------------
     * Apply - Used for 'last order' functionality
     * Sets properties for the order - but does not overwrite existing properties
     */
    function apply(properties) {
        const properties_to_apply = [];

        if (properties.products !== undefined && !getProductsQuantity()) {
            properties_to_apply.push('products');

            // Strip any product or modifier unique ids
            properties.products = _.map(properties.products, function(product) {
                product.id = null;
                product.modifiers = _.map(product.modifiers, function(
                    modifier
                ) {
                    modifier.id = null;
                    return modifier;
                });
                return product;
            });
        }

        if (!location) {
            properties_to_apply.push('location');
        }

        if (!method) {
            properties_to_apply.push('method');
        }

        if (!address && properties.address) {
            properties_to_apply.push('address');
        }

        if (!comment && properties.comment) {
            properties_to_apply.push('comment');
        }

        set(_.pick(properties, properties_to_apply));

        return self;
    }

    /*--------------------------------------------------------------------------
     */
    function isTest() {
        return is_test;
    }

    function setIsTest(new_is_test) {
        if (is_test !== new_is_test) {
            is_test = new_is_test;
            trigger('updateIsTest', is_test);
        }
    }

    /*--------------------------------------------------------------------------
     * Customer
     */
    function setCustomer(new_customer) {
        if (
            new_customer &&
            !(new_customer instanceof Mobi2Go._class.Customer)
        ) {
            throw new TypeError(
                'customer must be an instance of Mobi2Go._class.Customer'
            );
        }

        if (
            (!new_customer && !customer) ||
            (customer && new_customer && customer.id === new_customer.id)
        ) {
            return self;
        }

        if (customer) {
            customer.unbind({
                updateRewards: updateAvailableRewards,
                updateAddresses: updateAvailableAddresses,
                updatePassword: onCustomerPasswordUpdate,
            });
        }

        customer = new_customer || null;

        if (customer) {
            customer.bind({
                updateRewards: updateAvailableRewards,
                updateAddresses: updateAvailableAddresses,
                updatePassword: onCustomerPasswordUpdate,
            });
        }

        trigger('updateCustomer', customer);

        return self;
    }

    function providePassword(password, callback) {
        return (
            customer &&
            customer.providePassword(password, function(is_valid) {
                trigger('passwordProvided', is_valid);

                if (_.isFunction(callback)) {
                    callback(is_valid);
                }
            })
        );
    }

    function provideFacebookAuthentication(authorisation, callback) {
        return (
            customer &&
            customer.provideFacebookAuthentication(authorisation, function(
                is_valid
            ) {
                trigger('passwordProvided', is_valid);

                if (_.isFunction(callback)) {
                    callback(is_valid);
                }
            })
        );
    }

    function provideAppleAuthentication(authorisation, callback) {
        return (
            customer &&
            customer.provideAppleAuthentication(authorisation, function(
                is_valid
            ) {
                trigger('passwordProvided', is_valid);

                if (_.isFunction(callback)) {
                    callback(is_valid);
                }
            })
        );
    }

    function provideGoogleAuthentication(authorisation, callback) {
        return (
            customer &&
            customer.provideGoogleAuthentication(authorisation, function(
                is_valid
            ) {
                trigger('passwordProvided', is_valid);

                if (_.isFunction(callback)) {
                    callback(is_valid);
                }
            })
        );
    }

    function onCustomerPasswordUpdate() {
        if (customer) {
            trigger('passwordProvided', customer.isPasswordProvided());
        }
    }

    function isPasswordProvided() {
        return !!(customer && customer.isPasswordProvided());
    }

    function sendReceiptToEmailAddress(email_address) {
        return new Promise((resolve, reject) => {
            Mobi2Go.app.serverRequest({
                api_version: 1,
                endpoint: ['orders', id, 'receipt', 'email'],
                type: 'POST',
                data: { email_address },
                success: resolve,
                error: reject,
            });
        });
    }

    /*--------------------------------------------------------------------------
     * Location
     */
    function setLocation(new_location) {
        if (
            new_location &&
            !(new_location instanceof Mobi2Go._class.Location)
        ) {
            throw new TypeError(
                'location must be an instance of Mobi2Go._class.Location'
            );
        }

        if (
            (!location && !new_location) ||
            (location && new_location && location.id === new_location.id)
        ) {
            return self;
        }

        if (
            new_location &&
            !is_test &&
            (new_location.isInactive() ||
                new_location.isHidden() ||
                new_location.isLockedOut())
        ) {
            trigger('locationOfflineWarning', new_location);
            return self;
        }

        // If the location has inactive methods warn the customer but don't stop initialisation
        if (new_location && new_location.hasInactiveMethods()) {
            trigger('methodsInactiveWarning', new_location);
        }

        if (location) {
            location.mute = true;
            location.stopAutoLoading();
            location.unbind({
                updateCurrentTime: onLocationUpdateCurrentTime,
                updateMenuRestrictions,
            });
        }

        location = new_location || null;

        if (location) {
            location.load();
            location.mute = false;
            location.startAutoLoading();
            location.bind({
                updateCurrentTime: onLocationUpdateCurrentTime,
                updateMenuRestrictions,
            });

            // Most of the storefront still looks at the tax object for the tax exclusive setting; as a workaround
            // this sets the correct flag on all tax objects
            location.sales_tax.has_tax_exclusive_prices =
                location.has_tax_exclusive_prices;
            location.discount_sales_tax.has_tax_exclusive_prices =
                location.has_tax_exclusive_prices;
            location.surcharge_sales_tax.has_tax_exclusive_prices =
                location.has_tax_exclusive_prices;
            location.tips_sales_tax.has_tax_exclusive_prices =
                location.has_tax_exclusive_prices;
            location.delivery_sales_tax.has_tax_exclusive_prices =
                location.has_tax_exclusive_prices;

            total.setTax(location.sales_tax);
            products.updateTax();
            discounts_total.setTax(location.discount_sales_tax);
            surcharge.setTax(location.surcharge_sales_tax);
            tip.setTax(location.tips_sales_tax);
            delivery_fee.setTax(location.delivery_sales_tax);

            if (menu_tier_id !== location.menu_tier_id) {
                menu_tier_id = location.menu_tier_id;
                trigger('updateMenuTierId', menu_tier_id);
            }
        }

        trigger('updateLocation', location);

        return self;
    }
    function setLocationById(id) {
        if (location && location.id === id) return self;
        const new_location = Mobi2Go.Locations.getById(id);
        return setLocation(new_location);
    }
    function setLocationByName(name) {
        if (location && location.name === name) return self;
        const new_location = Mobi2Go.Locations.getByName(name);
        return setLocation(new_location);
    }

    function getAvailableDeliveryAreas() {
        return location && method === 'delivery' ? location.delivery_areas : [];
    }

    function updateAvailableDeliveryAreas() {
        const prev_areas = available_delivery_areas;
        available_delivery_areas = getAvailableDeliveryAreas();

        if (!_.isEqual(prev_areas, available_delivery_areas)) {
            trigger('updateAvailableDeliveryAreas', available_delivery_areas);
        }
    }

    function onLocationUpdateCurrentTime() {
        if (!method || !expected_at || getProgressPhase() === 'confirmed') {
            return;
        }

        if (server_time_slots) {
            // If the time slots are syncing the next bit of logic will
            // not yield the correct result therefore we skip it.
            // On sync completion the available dates and times
            // will update.
            if (time_slots.isSyncing()) {
                return;
            }

            // Look through the time slots, mark them dirty if one
            // of them is in the past.
            // This operation is synchronous.
            time_slots.refresh();

            // Fetch new time slots if it's been too long since
            // the last fetch or if they're marked dirty.
            if (time_slots.shouldSync()) {
                fetchTimeSlots();
                return;
            }
        }

        // We generate a random amount of time to wait before preceeding here.
        // Because this call happens at at regular interval and can trigger API
        // requests, adding randomness here prevents large numbers of storefront
        // clients updating their expect time at the same time, which can create
        // large spikes in web API requests.
        const staggered_timeout = _.random(0, 60 * 2) * 1000;
        const is_expected_time_update_required =
            !expected_at || expected_at.isBefore(getAsapExpectedTime(method));

        setTimeout(
            () => {
                // If expected_at is behind the asap due to 4th dimension progression
                const asap_datetime = getAsapExpectedTime(method);
                if (!expected_at || expected_at.isBefore(asap_datetime)) {
                    setExpectedAt(asap_datetime);
                }

                updateAvailableDates();
                updateAvailableTimes();
                updateMinutesToClosing();
                updateExpectedInString();
            },
            is_expected_time_update_required ? staggered_timeout : 0
        );
    }

    /*--------------------------------------------------------------------------
     * Address
     */
    function getAddress() {
        return address;
    }
    function setAddress(new_address) {
        if (
            new_address &&
            !(new_address instanceof Mobi2Go._class.CustomerAddress)
        ) {
            throw new TypeError(
                'address must be an instance of Mobi2Go._class.CustomerAddress'
            );
        }

        if (
            (!address && !new_address) ||
            (address && new_address && address.id === new_address.id)
        ) {
            return self;
        }

        if (
            new_address &&
            (!location || !location.isAddressAvailable(new_address))
        ) {
            return self;
        }

        if (address) {
            address.unbind('update', triggerUpdateAddress);
        }

        address = new_address;

        if (address) {
            address.bind('update', triggerUpdateAddress);
        }

        triggerUpdateAddress();
        return self;
    }

    function triggerUpdateAddress() {
        trigger('updateAddress', address);
    }

    function setAddressById(id) {
        if (address && address.id === id) {
            return self;
        }
        const new_address = customer ? customer.getAddressById(id) : null;
        return setAddress(new_address);
    }

    function setAddressForGuest(new_address) {
        if (new_address.exclusive_order_id !== id) {
            return;
        }
        if (new_address.id !== undefined) {
            const new_address_instance = customer.getAddressById(
                new_address.id
            );
            if (new_address_instance) {
                setAddress(new_address_instance);
                return;
            }
        }
        customer.createAddress(new_address, setAddress);
    }

    function getAvailableAddresses() {
        if (location && customer && method === 'delivery') {
            return location.filterAvailableAddresses(customer.addresses);
        }

        return [];
    }
    function updateAvailableAddresses() {
        const prev_addresses = available_addresses;
        available_addresses = getAvailableAddresses();

        if (!_.isEqual(prev_addresses, available_addresses)) {
            trigger('updateAvailableAddresses', available_addresses);

            if (
                !available_addresses.length ||
                (address &&
                    (!location || !location.isAddressAvailable(address)))
            ) {
                setAddress(null);
            }
        }
    }

    function setAddressAfterCustomerSet(address) {
        if (customer) {
            createAddressAndSet();
        } else {
            unbind('updateCustomer', setAddressAfterCustomerSet.callback);
            bind(
                'updateCustomer',
                (setAddressAfterCustomerSet.callback = function(customer) {
                    if (customer && location && method) {
                        createAddressAndSet();
                        unbind(
                            'updateCustomer',
                            setAddressAfterCustomerSet.callback
                        );
                    }
                })
            );
        }

        function createAddressAndSet() {
            address = customer.createAddress(address).save();
            setAddress(address);
        }
    }

    /**-------------------------------------------------------------------------
     * Menu
     */
    function getMenu() {
        return menu;
    }

    function getMenuTierId() {
        return location ? location.menu_tier_id : null;
    }

    function getMenuLevelId() {
        return menu_level_id;
    }

    function setMenuLevelId(new_menu_level_id) {
        if (new_menu_level_id === menu_level_id) {
            return;
        }

        menu_level_id = new_menu_level_id;
        trigger('updateMenuLevelId', menu_level_id);
    }

    function setAvailableMenus(new_available_menus) {
        if (new_available_menus === null || new_available_menus.length === 0) {
            new_available_menus = Mobi2Go.Menu.getDefaultMenus();
        } else if (isPayAtTablePlatform()) {
            new_available_menus = Mobi2Go.Menu.getMenusByTag(
                PLATFORMS.PAYATTABLE
            );
        } else {
            new_available_menus = _.map(new_available_menus, function(
                new_available_menu
            ) {
                if (
                    new_available_menu instanceof Mobi2Go._class.MenuItem.Menu
                ) {
                    return new_available_menu;
                }
                return Mobi2Go.Menu.getMenus().getById(new_available_menu.id);
            });
        }

        if (
            !_.isEqual(
                _.map(new_available_menus, 'id'),
                _.map(available_menus, 'id')
            )
        ) {
            available_menus = new_available_menus;
            trigger('updateAvailableMenus', available_menus);

            if (!menu) {
                setMenu(available_menus[0]);
                return;
            }

            if (!is_test && !isMenuAvailable(menu)) {
                const menu_with_matching_name = _.find(available_menus, {
                    name: menu.name,
                });
                if (menu_with_matching_name) {
                    setMenu(menu_with_matching_name);
                } else {
                    setMenu(available_menus[0]);
                }
            }
        }
    }

    let customMenuSelector = null;
    function updateAvailableMenus() {
        setAvailableMenus(location ? location.getAvailableMenus() : null);
    }

    function isMenuAvailable(menu) {
        return (
            menu && menu.id && _.includes(_.map(available_menus, 'id'), menu.id)
        );
    }

    function getAvailableMenus() {
        return available_menus;
    }

    function setMenu(provided_menu) {
        if (!provided_menu) {
            return;
        }

        let new_menu = null;
        const menus = Mobi2Go.Menu.getMenus();

        if (provided_menu.id) {
            new_menu = menus.getById(provided_menu.id);
        } else if (provided_menu.name) {
            new_menu = menus.getByName(provided_menu.name);
        }

        if (menu && new_menu && menu.id === new_menu.id) {
            return;
        }

        if (!(new_menu instanceof Mobi2Go._class.MenuItem.Menu)) {
            throw new TypeError(
                'menu must be an instance of Mobi2Go._class.MenuItem.Menu',
                { provided_menu }
            );
        }

        menu = new_menu;
        trigger('updateMenu', menu);
    }

    /**-------------------------------------------------------------------------
     */
    function getUnavailableProductsForMenus(menus) {
        _.each(menus, function(menu) {
            if (!(menu instanceof Mobi2Go._class.MenuItem.Menu)) {
                throw new TypeError(
                    'menu must be an instance of Mobi2Go._class.MenuItem.Menu'
                );
            }
        });

        return _.filter(products.get(), function(product) {
            for (let i = 0; i < menus.length; ++i) {
                if (
                    menus[i].getProductByDisplayName(product.menu_product.name)
                ) {
                    return false;
                }
            }
            return true;
        });
    }

    /**-------------------------------------------------------------------------
     */
    function getUnavailableProductsForMenu(menu) {
        if (!(menu instanceof Mobi2Go._class.MenuItem.Menu)) {
            throw new TypeError(
                'menu must be an instance of Mobi2Go._class.MenuItem.Menu'
            );
        }

        return _.filter(products.get(), function(product) {
            return !menu.getProductByDisplayName(product.menu_product.name);
        });
    }

    /**-------------------------------------------------------------------------
     */
    function getUnavailableAndRestrictedProductsForLocation(location) {
        if (location && !(location instanceof Mobi2Go._class.Location)) {
            throw new TypeError(
                'location must be an instance of Mobi2Go._class.Location'
            );
        }

        const unavailable_products = getUnavailableProductsForMenus(
            location.getAvailableMenus()
        );
        const oos_products = getRestrictedProductsForLocation(location);

        const products = _.union(unavailable_products, oos_products);

        return _.uniq(products, function(product) {
            return product.menu_product.id;
        });
    }

    /**-------------------------------------------------------------------------
     * If the menu of the order changes, ensure all products in the order are
     * available in the menu, if not, attempt to find replacement products or
     * then remove the product from the order
     */
    function switchOrRemoveProductsNotInLocationMenu() {
        if (!location) {
            return;
        }

        const set_products = [];
        const removed_products = [];

        _.each(products.get(), function(product) {
            // Product exists in this menu
            if (menu.containsProduct(product.menu_product)) {
                set_products.push(product);

                return;
            }

            // Switch product if product with an identical name is found in menu
            const switchable_menu_product = menu.getProductByDisplayName(
                product.menu_product.name
            );
            if (switchable_menu_product) {
                const modifiers = [];

                _.each(product.getAlteredModifiers(), function(modifier) {
                    const { name } = modifier.menu_modifier;
                    const switchable_menu_modifier = switchable_menu_product.getModifierByDisplayName(
                        name
                    );

                    if (switchable_menu_modifier) {
                        modifier.menu_modifier = switchable_menu_modifier;
                        modifiers.push(modifier);
                    }
                });

                product.set({
                    id: null,
                    menu_product: switchable_menu_product,
                    modifiers,
                });

                set_products.push(product);
                return;
            }

            // Remove product
            removed_products.push(product);
        });

        if (removed_products.length) {
            products.set(set_products);
            trigger('removedProductsNotInMenuWarning', removed_products);
        }
    }

    /*--------------------------------------------------------------------------
     */
    function getFirstProductWithUnmetModifierRequirements() {
        return _.find(products.get(), function(product) {
            return (
                product.getUnmetSelectionRequirementModifierGroup() !==
                    undefined ||
                product.getProductsWithUnmetSelectionRequirementModifierGroup()
                    .length > 0
            );
        });
    }

    /*--------------------------------------------------------------------------
     * Method
     */
    function setMethod(new_method) {
        const available_methods = getAvailableMethods();

        if (available_methods.length === 1 && new_method === null) {
            new_method = available_methods[0];
        }

        if (
            method === new_method ||
            (new_method && !_.includes(available_methods, new_method))
        ) {
            return self;
        }

        const is_asap_time = isExpectedAsap();

        method = new_method;
        trigger('updateMethod', method);

        // Preemptively set delivery fee to 0.00
        if (method !== 'delivery') {
            setDeliveryFee(0.0);
        }

        // Set curbside to empty if they changed it
        if (method !== 'pickup') {
            setCurbsidePickupDetails({
                colour: '',
                brand: '',
                make: '',
                plate_number: '',
            });
        }

        if (is_asap_time) {
            setExpectedAt(getAsapExpectedTime());
        }

        return self;
    }

    function getAvailableMethods() {
        return location ? location.getAvailableOrderMethods() : [];
    }
    function updateAvailableMethods() {
        const prev_methods = available_methods;
        available_methods = getAvailableMethods();

        if (_.isEqual(prev_methods, available_methods)) return;

        trigger('updateAvailableMethods', available_methods);

        if (available_methods.length === 1) {
            setMethod(available_methods[0]);
        } else if (method && !_.includes(available_methods, method)) {
            setMethod(null);
        }
    }

    /*--------------------------------------------------------------------------
     * External Progress
     */
    function setAvailablePostConfirmationStatuses(
        new_available_post_confirmation_statuses
    ) {
        const current_available_post_confirmation_statuses = available_post_confirmation_statuses;

        if (
            _.isEqual(
                current_available_post_confirmation_statuses,
                new_available_post_confirmation_statuses
            )
        ) {
            return;
        }

        available_post_confirmation_statuses = new_available_post_confirmation_statuses;

        trigger(
            'updateAvailablePostConfirmationStatuses',
            new_available_post_confirmation_statuses
        );
    }

    function getAvailablePostConfirmationStatuses() {
        return available_post_confirmation_statuses;
    }

    function setCurrentPostConfirmationStatus(new_post_confirmation_status) {
        const prev_post_confirmation_status = current_post_confirmation_status;

        if (
            _.isEqual(
                prev_post_confirmation_status,
                new_post_confirmation_status
            )
        ) {
            return;
        }

        current_post_confirmation_status = new_post_confirmation_status;

        trigger(
            'updateCurrentPostConfirmationStatus',
            new_post_confirmation_status
        );
    }

    function getCurrentPostConfirmationStatus() {
        return current_post_confirmation_status;
    }

    /*--------------------------------------------------------------------------
     */
    function setWaitTime(new_wait_time) {
        if (new_wait_time === wait_time) {
            return;
        }

        was_asap_time_before_wait_time_adjustment = isExpectedAsap();
        wait_time = new_wait_time;

        trigger('updateWaitTime', wait_time);
    }

    /*--------------------------------------------------------------------------
     */
    function getTableId() {
        return table_id;
    }

    function setTableId(new_table_id) {
        if (table_id !== new_table_id) {
            table_id = new_table_id;
            trigger('updateTableId', table_id);
        }
    }

    /*--------------------------------------------------------------------------
     * Delivery fee
     */
    function setDeliveryFee(new_delivery_fee) {
        if (!delivery_fee.isEqual(new_delivery_fee)) {
            delivery_fee.set(new_delivery_fee);
            trigger('updateDeliveryFee', delivery_fee);
        }
    }

    function setDeliveryFeeDiscount(new_delivery_fee_discount) {
        if (delivery_fee_discount.isEqual(new_delivery_fee_discount)) {
            return;
        }

        delivery_fee_discount.set(new_delivery_fee_discount);
        trigger('updateDeliveryFeeDiscount', delivery_fee_discount);
    }

    /*--------------------------------------------------------------------------
     */
    function getTotalForDeliveryMinimum() {
        const products_total = products
            .getTotal()
            .getTaxInclusivePrice()
            .toFloat();
        const tip_total = tip.getTaxInclusivePrice().toFloat();
        const surcharge_total = surcharge.getTaxInclusivePrice().toFloat();
        const discounts_total_minus_delivery_discount =
            discounts_total.getTaxInclusiveDiscount().toFloat() -
            delivery_fee_discount.getTaxInclusiveDiscount().toFloat();

        const total_for_delivery_minimum =
            products_total +
            tip_total +
            surcharge_total -
            discounts_total_minus_delivery_discount;

        return total_for_delivery_minimum;
    }

    function isMeetingDeliveryMinimumProductsTotal() {
        if (method === 'delivery') {
            const total_for_delivery_minimum = getTotalForDeliveryMinimum();

            return (
                total_for_delivery_minimum >= delivery_minimum_total.toFloat()
            );
        }

        return true;
    }

    /*--------------------------------------------------------------------------
     */
    function getDeliveryMinimumTotal() {
        return delivery_minimum_total;
    }

    /*--------------------------------------------------------------------------
     */
    function setDeliveryMinimumTotal(total) {
        if (!delivery_minimum_total.isEqual(total)) {
            delivery_minimum_total.set(total);
            trigger('updateDeliveryMinimumTotal', delivery_minimum_total);
        }
    }

    /**-------------------------------------------------------------------------
     * Surcharge
     */
    function setSurcharge(new_surcharge) {
        if (!surcharge.isEqual(new_surcharge)) {
            surcharge.set(new_surcharge);
            trigger('updateSurcharge', surcharge);
        }
    }

    /**-------------------------------------------------------------------------
     * Tip
     */
    function setTip(new_tip, is_tax_exclusive_amount) {
        if (new_tip < 0) {
            new_tip = 0.0;
        } else if (new_tip > 1000) {
            new_tip = 1000;
        }

        if (
            (is_tax_exclusive_amount === undefined &&
                tip.isTaxExclusivePrice()) ||
            is_tax_exclusive_amount
        ) {
            // Clone tip to duplicate the right tax amount for tips
            new_tip = tip
                .clone()
                .set(new_tip)
                .applyTaxAmount();
        }

        if (!tip.isEqual(new_tip)) {
            tip.set(new_tip);
            trigger('updateTip', tip);
        }
    }

    function getTotalWithoutTip() {
        return Price(
            Math.round((total.toFloat() - tip.toFloat()) * 1000) / 1000
        );
    }

    // Default tip amount
    // Hardcode a 15% default for Canadian Door Dash Drive clients
    function getDefaultTip() {
        if (
            location?.delivery?.mode === 'door_dash_drive' &&
            method === 'delivery' &&
            Mobi2Go.headoffice.country_iso3166_alpha2 === 'CA'
        ) {
            const default_tip_percentage = 15;
            const default_tip_amount =
                getTotalWithoutTip().toFloat() * (default_tip_percentage / 100);

            return Math.round(default_tip_amount * 100) / 100;
        }

        return null;
    }

    function shouldPromptForTip() {
        return (
            location &&
            location.has_tips_enabled &&
            !Mobi2Go.app.state.is_part_of_group_tab &&
            !Mobi2Go.Customer.getCurrentGroupTab()
        );
    }

    function shouldPromptForGifting() {
        return false;
    }

    function shouldSkipConfirmationAndPaymentPage() {
        return isKioskPlatform();
    }

    /**
     * Language
     */
    function setLanguage(new_language) {
        if (new_language !== Mobi2Go.app.language) {
            new_language = Mobi2Go.app.language;
        }

        if (language !== new_language) {
            language = new_language;
            trigger('updateLanguage', language);
        }
    }

    /**
     * Credit
     *
     * Note on credit: The Mobi2Go Web API allows an abitrary amount of credit to be set
     * for an order. However, this functionality is not supported here on the front-end.
     * Instead, for simplicity, the maximum amount of available credit is always applied to the order.
     */
    function setCredit(amount) {
        if (
            (!Mobi2Go.headoffice.credit_program.enabled &&
            // Loyalty program that uses credit
            customer
                ? !_.reduce(
                      customer.getLoyalty(),
                      function(result, scheme) {
                          return result || scheme.features?.uses_credit;
                      },
                      false
                  )
                : null) ||
            amount < 0 ||
            !customer ||
            payment.method !== 'online'
        ) {
            amount = 0.0;
        }

        if (customer && amount > customer.getCreditBalance().toFloat()) {
            amount = customer.getCreditBalance().toFloat();
        }

        if (amount > total.toFloat()) {
            amount = total.toFloat();
        }

        if (amount < Mobi2Go.headoffice.credit_program.minimum_spend) {
            amount = 0.0;
        }

        if (!credit.isEqual(amount)) {
            credit.set(amount);
            trigger('updateCredit', credit);
        }
    }

    function automaticallyAdjustCredit() {
        if (
            unapplyCredit.set ||
            ['confirmed', 'confirming'].includes(getProgressPhase())
        ) {
            return;
        }

        if (!customer) {
            setCredit(0.0);
            return;
        }

        applyAvailableCredit();
    }

    function applyAvailableCredit() {
        if (customer && customer.getCreditBalance().toFloat()) {
            setCredit(customer.getCreditBalance().toFloat());
        }
    }

    function unapplyCredit() {
        setCredit(0.0);
        unapplyCredit.set = true;
    }

    function setRebates(rebatesToSet) {
        if (!_.isEqual(rebates, rebatesToSet)) {
            rebates = rebatesToSet;
            setRebatesAppliedTotal();
            trigger('updatedRebates', rebates);
        }
    }

    function setRebatesAppliedTotal() {
        // Calculate total applied.
        rebates_applied_total.set(
            _.reduce(
                rebates,
                function (sum, rebate) {
                    return sum + (
                        rebate.is_claimed
                        ? parseFloat(rebate.order_amount_discounted)
                        : 0
                    );
                },
                0
            )
        );
    }

    const claimRebate = async balance_reference => {
        return new Promise((resolve, reject) => {
            Mobi2Go.app.serverRequest({
                api_version: 1,
                endpoint: ['orders', Mobi2Go.Order.getId(), 'rebates'],
                type: 'POST',
                data: {
                    balance_reference,
                },
                success: response => {
                    if (response.hasOwnProperty('rebate')) {
                        Mobi2Go.Customer.trigger('updateBalances');
                        resolve(response.rebate);
                    }
                    reject('No rebate in response');
                },
                error: reject,
            });
        });
    };

    const cancelRebate = async rebate_id => {
        return new Promise((resolve, reject) => {
            Mobi2Go.app.serverRequest({
                api_version: 1,
                endpoint: [
                    'orders',
                    Mobi2Go.Order.getId(),
                    'rebates',
                    rebate_id,
                ],
                type: 'DELETE',
                success: response => {
                    Mobi2Go.Customer.trigger('updateBalances');
                    resolve(response);
                },
                error: reject,
            });
        });
    };

    /*--------------------------------------------------------------------------
     * Datetime
     */
    let customAvailableTimesFilter = null;
    let customAvailableDatesFilter = null;

    const isSyncingTimeSlots = () => {
        return time_slots.isSyncing();
    };

    const invalidateTimeSlots = () => {
        time_slots.invalidate();
        time_slots.setIsSyncing(true);
    };

    const fetchTimeSlots = () => {
        if (!is_ready) {
            return;
        }
        invalidateTimeSlots();
        load();
    };

    const ensureExpectedAtIsAvailable = () => {
        // Trigger on the next tick because time_slots are set before the expected_at in Order.set
        // This will prevent the expected at from being overridden.
        setTimeout(() => {
            const forward_only = true;
            const new_expected_at = getClosestExpectedTime(
                expected_at,
                forward_only
            );
            if (new_expected_at && !new_expected_at.isSame(expected_at)) {
                setExpectedAt(new_expected_at);
            }
        }, 0);
    };

    const momentComparator = granularity => (a, b) => {
        if (a instanceof moment && b instanceof moment) {
            return a.isSame(b, granularity);
        }
        return undefined;
    };

    const getAvailableDates = () => {
        if (!location || !method) {
            return [];
        }

        let dates = server_time_slots
            ? time_slots.getDates()
            : location.getAvailableOrderDates(method);

        if (earliest_expected_at !== null) {
            dates = dates.filter(
                date => !date.isBefore(earliest_expected_at, 'day')
            );
        }

        if (customAvailableDatesFilter) {
            dates = customAvailableDatesFilter(dates);
        }

        return dates;
    };

    const updateAvailableDates = () => {
        const new_available_dates = getAvailableDates();
        if (
            _.isEqualWith(
                available_dates,
                new_available_dates,
                momentComparator('day')
            )
        ) {
            return;
        }

        available_dates = new_available_dates;
        trigger('updateAvailableDates', available_dates);

        if (!available_dates.length) {
            setExpectedAt(null);
        } else if (!expected_at) {
            updateAvailableTimes();
        }
    };

    const getAvailableTimes = date => {
        if (!location || !method) {
            return [];
        }

        let times = server_time_slots
            ? time_slots.getTimes(date || expected_at)
            : location.getAvailableOrderTimes(method, date || expected_at);

        if (earliest_expected_at !== null) {
            times = times.filter(time => !time.isBefore(earliest_expected_at));
        }

        if (customAvailableTimesFilter) {
            return customAvailableTimesFilter(times);
        }

        return times;
    };

    const updateAvailableTimes = () => {
        const new_available_times = getAvailableTimes();
        if (
            _.isEqualWith(
                new_available_times,
                available_times,
                momentComparator('minute')
            )
        ) {
            return;
        }

        available_times = new_available_times;
        trigger('updateAvailableTimes', available_times);

        if (!available_times.length) {
            setExpectedAt(null);
        } else if (!expected_at) {
            setExpectedAt(available_times[0]);
        } else if (
            was_asap_time_before_wait_time_adjustment ||
            was_asap_time_before_earliest_expected_at_adjustment
        ) {
            // This is a mechanism to ensure that the order will stick to the
            // ASAP expected time when wait times are adjusted
            const asap = getAsapExpectedTime();
            setExpectedAt(asap);
            was_asap_time_before_wait_time_adjustment = null;
            was_asap_time_before_earliest_expected_at_adjustment = null;
        }
    };

    const isDateInAvailableDates = date => {
        return _.some(available_dates, available_date =>
            available_date.isSame(date, 'day')
        );
    };

    function setExpectedAt(new_datetime, skip_date_validation) {
        if (new_datetime && location) {
            new_datetime = Mobi2Go._class.Location.prototype.toOrderDatetime(
                new_datetime
            );
        }

        if (
            (!new_datetime && !expected_at) ||
            +new_datetime === +expected_at ||
            (new_datetime && (!location || !method))
        ) {
            return self;
        }

        if (
            !skip_date_validation &&
            new_datetime &&
            !isDateInAvailableDates(new_datetime)
        ) {
            new_datetime = getAsapExpectedTime();
        }

        expected_at = new_datetime;

        /**
         * updateAvailableTimes will try to stick to the ASAP expected time if
         * the wait times or earliest expected at were adjusted by the backend.
         * If the customer changes the expected time after that then the
         * two variables should be updated accordingly to prevent updateAvailableTimes from
         * resetting to ASAP expected time when that's not what the customer wants.
         */
        const is_expected_asap = isExpectedAsap();
        was_asap_time_before_earliest_expected_at_adjustment =
            was_asap_time_before_earliest_expected_at_adjustment &&
            is_expected_asap;
        was_asap_time_before_wait_time_adjustment =
            was_asap_time_before_wait_time_adjustment && is_expected_asap;

        trigger('updateExpectedAt', expected_at);

        return self;
    }

    const getMinutesToExpectedAt = () => {
        if (!location || !method || !expected_at) {
            return null;
        }
        return Math.abs(location.current_time.diff(expected_at, 'minutes'));
    };

    const getClosestExpectedTime = (datetime, forward_only = false) => {
        let closest_time;
        let min_diff;
        available_dates.forEach(available_date => {
            // discard dates prior to datetime's day
            if (available_date.isBefore(datetime, 'day')) {
                return;
            }
            // discard dates where the start of the day is already
            // further away than the minumum difference found until
            // now.
            if (
                min_diff !== undefined &&
                Math.abs(available_date.diff(datetime)) > min_diff
            ) {
                return;
            }
            const available_times_for_date = getAvailableTimes(available_date);
            available_times_for_date.forEach(available_time => {
                if (forward_only && available_time.isBefore(datetime)) {
                    return;
                }
                const diff = Math.abs(available_time.diff(datetime));
                if (min_diff === undefined || diff < min_diff) {
                    closest_time = available_time;
                    min_diff = diff;
                }
            });
        });
        return closest_time;
    };

    const getAsapExpectedTime = () => {
        if (!method || !location) {
            return undefined;
        }
        const asap_expected_time = server_time_slots
            ? time_slots.getFirstTimeSlot()
            : location.getAsapOrderDatetime(method);

        if (
            earliest_expected_at &&
            asap_expected_time &&
            asap_expected_time.isBefore(earliest_expected_at)
        ) {
            return getClosestExpectedTime(earliest_expected_at, true);
        }

        return asap_expected_time;
    };

    function isExpectedAsap() {
        return (
            location &&
            method &&
            expected_at &&
            expected_at.isSame(getAsapExpectedTime())
        );
    }
    function isScheduled() {
        return (
            method && expected_at && !expected_at.isSame(getAsapExpectedTime())
        );
    }
    function isExpectedToday() {
        return (
            method &&
            expected_at &&
            location &&
            expected_at.isSame(location.current_time, 'day')
        );
    }
    function isExpectedTomorrow() {
        if (method && expected_at && location) {
            const tomorrow = location.current_time.clone().add(1, 'day');
            return tomorrow.isSame(expected_at, 'day');
        }

        return false;
    }

    const getExpectedInString = () => expected_in_string;

    const setExpectedInString = expected_in => {
        if (expected_in !== expected_in_string) {
            expected_in_string = expected_in;
            trigger('updateExpectedInString', expected_in_string);
        }
    };

    const updateExpectedInString = () => {
        let expected_in = null;
        if (location && expected_at) {
            expected_in = expected_at.from(location.current_time, true);

            if (expected_at.diff(location.current_time, 'hours') > 0) {
                expected_in = i18n.t('order.datetime:expected_in_about', {
                    expected_in,
                });
            } else {
                expected_in = i18n.t('order.datetime:expected_in', {
                    expected_in,
                });
            }
        }
        setExpectedInString(expected_in);
    };

    function setEarliestExpectedAt(new_earliest_expected_at) {
        if (!location || !method) {
            new_earliest_expected_at = null;
        }

        if (new_earliest_expected_at) {
            new_earliest_expected_at = Mobi2Go._class.Location.prototype.toOrderDatetime(
                new_earliest_expected_at
            );
        } else {
            new_earliest_expected_at = null;
        }

        if (!earliest_expected_at && !new_earliest_expected_at) {
            return;
        }

        if (
            earliest_expected_at &&
            new_earliest_expected_at &&
            earliest_expected_at.isSame(new_earliest_expected_at)
        ) {
            return;
        }

        was_asap_time_before_earliest_expected_at_adjustment = isExpectedAsap();

        earliest_expected_at = new_earliest_expected_at;

        trigger('updateEarliestExpectedAt', earliest_expected_at);
    }

    /**-------------------------------------------------------------------------
     * Minutes until the location closes for the day
     * Used to prompt customers to confirm their orders before store closes
     */
    const getMinutesToClosing = () => minutes_to_closing;

    const setMinutesToClosing = minutes => {
        if (minutes_to_closing !== minutes) {
            minutes_to_closing = minutes;
            trigger('updateMinutesToClosing', minutes);
        }
    };

    const updateMinutesToClosing = () => {
        if (!location || !method || !expected_at) {
            return;
        }

        let seconds_remaining;
        if (server_time_slots) {
            const last_time_slot = time_slots.getLastTimeSlotOfFirstDay();
            seconds_remaining = last_time_slot
                ? last_time_slot.diff(location.current_time, 'seconds')
                : 0;
            if (wait_time !== null) {
                seconds_remaining -= wait_time * 60;
            }
        } else {
            seconds_remaining = location.getSecondsToFinalOrderTimeCutOff(
                method,
                expected_at
            );
        }

        seconds_remaining -= MINUTES_TO_CLOSING_BUFFER * 60;

        let new_minutes_to_closing = Math.floor(seconds_remaining / 60);

        // Seconds to closing isn't 100% accurate and can return negative values.
        // This make things a bit simplier
        if (seconds_remaining < 120) {
            new_minutes_to_closing = 1;
        }

        setMinutesToClosing(new_minutes_to_closing);
    };

    /*--------------------------------------------------------------------------
     * Products
     */
    function getProducts() {
        return products.get();
    }
    function addProduct(product) {
        products.add(product);
        return this;
    }
    function removeProduct(product) {
        products.remove(product);
        return this;
    }
    function containsProduct(product) {
        return products.contains(product);
    }
    function getProductsTotal() {
        return products.getTotal();
    }
    function getProductsQuantity() {
        return products.getQuantity();
    }
    function containsProduct(product) {
        return products.contains(product);
    }

    function isProductRestricted(menu_product, date) {
        if (!location) {
            return false;
        }

        if (!(menu_product instanceof Mobi2Go._class.MenuItem.Product)) {
            menu_product = Mobi2Go.Menu.getProducts().getById(menu_product.id);
        }

        date = date || expected_at;
        return location.isProductRestricted(menu_product, date);
    }

    function isProductSoldOut(menu_product, date) {
        if (!location) {
            return false;
        }

        if (!(menu_product instanceof Mobi2Go._class.MenuItem.Product)) {
            menu_product = Mobi2Go.Menu.getProducts().getById(menu_product.id);
        }

        date = date || expected_at;
        return location.isProductSoldOut(menu_product, date);
    }

    function isProductHidden(menu_product, date) {
        if (!location) {
            return false;
        }

        if (!(menu_product instanceof Mobi2Go._class.MenuItem.Product)) {
            menu_product = Mobi2Go.Menu.getProducts().getById(menu_product.id);
        }

        date = date || expected_at;
        return location.isProductHidden(menu_product, date);
    }

    function isModifierGroupRestricted(menu_modifier_group, date) {
        if (!location) {
            return false;
        }

        date = date || expected_at;
        return location.isModifierGroupRestricted(menu_modifier_group, date);
    }

    function isModifierRestricted(menu_modifier, date) {
        if (!location) {
            return false;
        }

        date = date || expected_at;
        return location.isModifierRestricted(menu_modifier, date);
    }

    function isModifierSoldOut(menu_modifier, date) {
        if (!location) {
            return false;
        }

        date = date || expected_at;
        return location.isModifierSoldOut(menu_modifier, date);
    }

    function isModifierHidden(menu_modifier, date) {
        if (!location) {
            return false;
        }

        date = date || expected_at;
        return location.isModifierHidden(menu_modifier, date);
    }

    /**-------------------------------------------------------------------------
     */
    function getMenuRestrictions() {
        return location ? location.menu_restrictions : [];
    }

    function updateMenuRestrictions() {
        const new_menu_restrictions = getMenuRestrictions();
        if (!_.isEqual(new_menu_restrictions, menu_restrictions)) {
            menu_restrictions = new_menu_restrictions;
            trigger('updateMenuRestrictions', menu_restrictions);
        }
    }

    function isProductRestrictedForLocation(product, location) {
        const size_modifier = product.size?.menu_modifier;
        return (
            location.isProductRestricted(product.menu_product, expected_at) ||
            !_.every(
                product.getAddedModifiers(),
                modifier =>
                    !location.isModifierRestricted(
                        modifier.menu_modifier,
                        expected_at
                    )
            ) ||
            (size_modifier &&
                location.isModifierRestricted(size_modifier, expected_at))
        );
    }

    function getRestrictedProductsForLocation(location) {
        if (location && !(location instanceof Mobi2Go._class.Location)) {
            throw new TypeError(
                'location must be an instance of Mobi2Go._class.Location'
            );
        }

        return _.filter(products.get(), product =>
            isProductRestrictedForLocation(product, location)
        );
    }

    function getRestrictedModifiersForLocationAndProduct(location, product) {
        if (location && !(location instanceof Mobi2Go._class.Location)) {
            throw new TypeError(
                'location must be an instance of Mobi2Go._class.Location'
            );
        }
        if (product && !(product instanceof Mobi2Go._class.OrderProduct)) {
            throw new TypeError(
                'product must be an instance of Mobi2Go._class.OrderProduct'
            );
        }

        return _.filter(product.getAddedModifiers(), function(modifier) {
            return location.isModifierRestricted(
                modifier.menu_modifier,
                expected_at
            );
        });
    }

    function removeRestrictedProducts() {
        if (!location || getProgressPhase() !== 'unconfirmed') {
            return;
        }

        const set_products = [];
        const removed_products = [];

        _.each(products.get(), function(product) {
            // Keep the product if it isn't restricted and doesn't have any restricted modifiers
            if (!isProductRestrictedForLocation(product, location)) {
                set_products.push(product);
            } else {
                removed_products.push(_.clone(product));
            }
        });

        if (removed_products.length) {
            products.set(set_products);
            trigger('restrictedProductsRemovedWarning', removed_products);
        }
    }

    /*--------------------------------------------------------------------------
     * Product suggestions
     */
    function getProductSuggestions() {
        if (product_suggestions.length === 0) {
            return product_suggestions_override;
        }

        return product_suggestions;
    }

    function setProductSuggestions(new_product_suggestions) {
        if (!_.isEqual(product_suggestions, new_product_suggestions)) {
            product_suggestions = new_product_suggestions;

            trigger('updateProductSuggestions', getProductSuggestions());
        }
    }

    function overrideProductSuggestions(new_product_suggestions_override) {
        if (
            !_.isEqual(
                new_product_suggestions_override,
                product_suggestions_override
            )
        ) {
            product_suggestions_override = new_product_suggestions_override;

            if (product_suggestions.length === 0) {
                trigger('updateProductSuggestions', getProductSuggestions());
            }
        }
    }

    function disableProductSuggestionsOverride() {
        // Placeholder for custom scripts
    }

    function updateProductSuggestionsOnProductAdd(new_product) {
        if (product_suggestions.length < 1) {
            return;
        }

        let new_product_suggestions = _.map(product_suggestions, function(
            suggestion
        ) {
            if (suggestion.category) {
                const categories = menu.getCategories();
                const menu_category = categories.getById(
                    suggestion.category.menu_category.id
                );

                if (
                    menu_category &&
                    menu_category.containsProduct(new_product.menu_product)
                ) {
                    // Check modifiers hereee
                    suggestion.category.quantity -= new_product.quantity;
                }
            } else if (suggestion.product) {
                if (
                    suggestion.product.menu_product.id ===
                    new_product.menu_product.id
                ) {
                    suggestion.product.quantity -= new_product.quantity;
                }
            }

            return suggestion;
        });

        new_product_suggestions = _.filter(new_product_suggestions, function(
            product_suggestion
        ) {
            if (product_suggestion.category) {
                return product_suggestion.category.quantity > 0;
            }
            if (product_suggestion.product) {
                return product_suggestion.product.quantity > 0;
            }

            return true;
        });

        setProductSuggestions(new_product_suggestions);
    }

    /*--------------------------------------------------------------------------
     * Payment
     */
    function setPayment(payment) {
        if (payment.total !== undefined) {
            setPaymentTotal(payment.total);
        }
        if (payment.available_processors !== undefined) {
            setAvailablePaymentProcessors(payment.available_processors);
        }
        if (payment.processor !== undefined) {
            setPaymentProcessor(payment.processor);
        }
        if (payment.processor_transaction !== undefined) {
            setPaymentProcessorTransaction(payment.processor_transaction);
        }
        if (payment.card_token_saving_enabled !== undefined) {
            setPaymentCardTokenSavingEnabled(payment.card_token_saving_enabled);
        }
        if (payment.method !== undefined) {
            setPaymentMethod(payment.method);
        }
        if (payment.available_card_tokens !== undefined) {
            setAvailablePaymentCardTokens(payment.available_card_tokens);
        }
        if (payment.card_token_id !== undefined) {
            setPaymentCardTokenById(payment.card_token_id);
        }
    }

    function setPaymentTotal(amount) {
        if (!payment.total.isEqual(amount)) {
            payment.total.set(amount);
            trigger('updatePaymentTotal', payment.total);
        }
    }

    function updatePaymentTotal() {
        const payment_total = total.clone();
        payment_total.subtract(credit);
        setPaymentTotal(payment_total);
    }

    function setPaymentOption(option) {
        setPaymentMethod(option.method);
        setPaymentProcessor({ name: option.processor });
        setPaymentCardToken(option.token);
    }

    function getPaymentOption() {
        const token = getPaymentCardToken();
        const processor = payment.processor ? payment.processor.name : null;

        if (!payment.method || (payment.method === 'online' && !processor)) {
            return null;
        }

        return {
            id: token
                ? `token${token.id}${token.payment_processor}`
                : payment.method +
                  (payment.method === 'online' ? processor : ''),
            method: payment.method,
            token,
            processor,
        };
    }

    function updatePaymentOption() {
        trigger('updatePaymentOption', getPaymentOption());
    }

    /**-------------------------------------------------------------------------
     * Turn various payment options into a simple and friendly list
     */
    function getAvailablePaymentOptions() {
        return available_payment_options;
    }

    function updateAvailablePaymentOptions() {
        const prev_available_payment_options = available_payment_options;

        const options = [];
        const available_payment_processors = getAvailablePaymentProcessors();
        const available_payment_tokens = getAvailablePaymentCardTokens();

        if (isPaymentMethodAvailable('online')) {
            _.each(available_payment_tokens, function(token) {
                options.push({
                    id: `token${token.id}${token.payment_processor}`,
                    method: 'online',
                    token,
                    processor: token.payment_processor,
                });
            });

            _.each(available_payment_processors, function(processor) {
                options.push({
                    id: `online${processor.name}`,
                    method: 'online',
                    token: null,
                    processor: processor.name,
                });
            });
        }

        if (isPaymentMethodAvailable('offline')) {
            options.push({
                id: 'offline',
                method: 'offline',
                token: null,
                processor: null,
            });
        }

        options.sort(function(a, b) {
            if (a.processor === 'GroupTab') {
                return -1;
            }

            if (b.processor === 'GroupTab') {
                return 1;
            }

            if (
                a.processor === 'Mobi2GoApplePay' ||
                a.processor === 'Mobi2GoGooglePay'
            ) {
                return -1;
            }

            if (
                b.processor === 'Mobi2GoApplePay' ||
                b.processor === 'Mobi2GoGooglePay'
            ) {
                return 1;
            }

            if (a.processor && b.processor) {
                if (a.token) {
                    return -1;
                }

                if (b.token) {
                    return 1;
                }

                if (a.processor === 'Mobi2GoPayments') {
                    return -1;
                }

                if (b.processor === 'Mobi2GoPayments') {
                    return 1;
                }
            }

            return 0;
        });

        if (!_.isEqual(prev_available_payment_options, options)) {
            available_payment_options = options;
            trigger('updateAvailablePaymentOptions', available_payment_options);
        }
    }

    function isPaymentMethodAvailable(payment_method) {
        return _.includes(getAvailablePaymentMethods(), payment_method);
    }

    function setPaymentMethod(new_payment_method) {
        if (payment.method === new_payment_method) {
            return self;
        }
        if (
            new_payment_method &&
            !_.includes(getAvailablePaymentMethods(), new_payment_method)
        ) {
            return self;
        }

        payment.method = new_payment_method;
        trigger('updatePaymentMethod', payment.method);

        return self;
    }

    function getAvailablePaymentMethods() {
        return location ? location.getAvailablePaymentMethods(method) : [];
    }

    function updateAvailablePaymentMethods() {
        const prev_available_methods = available_payment_methods;
        available_payment_methods = getAvailablePaymentMethods();

        if (_.isEqual(prev_available_methods, available_payment_methods))
            return self;

        trigger('updateAvailablePaymentMethods', available_payment_methods);

        if (
            payment.method &&
            !_.includes(available_payment_methods, payment.method)
        ) {
            setPaymentMethod(null);
        }

        return self;
    }

    function getPaymentCardTokenSavingEnabled() {
        return payment.card_token_saving_enabled;
    }

    function setPaymentCardTokenSavingEnabled(is_enabled) {
        if (payment.card_token_saving_enabled !== is_enabled) {
            payment.card_token_saving_enabled = !!is_enabled;
            trigger('updatePaymentCardTokenSavingEnabled', !!is_enabled);
        }
    }

    function isPaymentCardTokenisationAvailable() {
        return !!(
            payment.processor &&
            payment.processor.is_card_tokenisation_available &&
            total.toFloat() > 0
        );
    }

    function setPaymentCardToken(token) {
        return setPaymentCardTokenById(token ? token.id : null);
    }

    function setPaymentCardTokenById(id) {
        const token = id ? getAvailablePaymentCardTokenById(+id) : null;
        if (
            (!payment.card_token_id && token) ||
            (payment.card_token_id && !token) ||
            (payment.card_token_id &&
                token &&
                payment.card_token_id !== token.id)
        ) {
            payment.card_token_id = token ? token.id : null;
            trigger('updatePaymentCardToken', token);

            if (token) {
                setPaymentProcessor({ name: token.payment_processor });
            }
        }

        return self;
    }

    function getPaymentCardToken() {
        if (payment.card_token_id) {
            return _.find(getAvailablePaymentCardTokens(), {
                id: payment.card_token_id,
            });
        }

        return null;
    }

    function getAvailablePaymentCardTokens() {
        return payment.available_card_tokens;
    }

    function getAvailablePaymentCardTokenById(id) {
        return _.find(getAvailablePaymentCardTokens(), { id });
    }

    function setAvailablePaymentCardTokens(available_payment_card_tokens) {
        if (
            !_.isEqual(
                payment.available_card_tokens,
                available_payment_card_tokens
            )
        ) {
            payment.available_card_tokens = available_payment_card_tokens;
            trigger(
                'updateAvailablePaymentCardTokens',
                payment.available_card_tokens
            );
        }
    }

    function getAvailablePaymentProcessors() {
        return payment.available_processors;
    }

    function getAvailablePaymentProcessorInstances() {
        return _.map(getAvailablePaymentProcessors(), function(processor) {
            return Mobi2Go._class.OrderPaymentProcessor(processor, self);
        });
    }

    function getPaymentProcessorInstanceByName(processor_name) {
        return _.find(getAvailablePaymentProcessorInstances(), function(
            processor
        ) {
            return processor.name === processor_name;
        });
    }

    function setAvailablePaymentProcessors(available_payment_processors) {
        available_payment_processors = _.filter(
            available_payment_processors,
            function(processor) {
                if (
                    processor.name === 'Mobi2GoApplePay' &&
                    window.applePayAvailable === false
                ) {
                    return false;
                }
                if (
                    processor.name === 'Mobi2GoGooglePay' &&
                    window.googlePayAvailable === false
                ) {
                    return false;
                }
                return true;
            }
        );
        if (
            !_.isEqual(
                payment.available_processors,
                available_payment_processors
            )
        ) {
            payment.available_processors = available_payment_processors;
            trigger(
                'updateAvailablePaymentProcessors',
                payment.available_processors
            );
        }
    }

    function setPaymentProcessor(processor) {
        if (processor !== null) {
            processor = Mobi2Go._class.OrderPaymentProcessor(processor, self);
        }

        if (
            (!payment.processor && !processor) ||
            (payment.processor &&
                processor &&
                payment.processor.name === processor.name &&
                payment.processor.is_card_tokenisation_available ===
                    processor.is_card_tokenisation_available &&
                _.isEqual(payment.processor._properties, processor._properties))
        ) {
            return self;
        }

        if (payment.processor) {
            payment.processor.detachFromOrder(self);
        }

        payment.processor = processor;
        trigger('updatePaymentProcessor', payment.processor);

        if (payment.processor) {
            payment.processor.attachToOrder(self);
        }

        return self;
    }

    function setPaymentProcessorTransaction(transaction) {
        if (payment.processor) {
            payment.processor.setTransaction(transaction);
        }
    }

    function isPasswordRequiredForPaymentToken(token) {
        token = token || getPaymentCardToken();

        if (!token || payment.total.toFloat() === 0.0) {
            return false;
        }

        const payment_processor = _.find(
            getAvailablePaymentProcessors(),
            function(processor) {
                return processor.name === token.payment_processor;
            }
        );
        if (!payment_processor) {
            return false;
        }

        return payment_processor.is_customers_password_required_for_tokenised_payments;
    }

    /*--------------------------------------------------------------------------
     * Comment
     */
    function setComment(new_comment) {
        if (comment === new_comment) {
            return self;
        }

        comment = new_comment;
        trigger('updateComment', comment);

        return self;
    }

    /*--------------------------------------------------------------------------
     * Metadata
     */
    function setMetadata(new_metadata) {
        if (_.isEqual(new_metadata, metadata)) {
            return self;
        }

        metadata = new_metadata;
        trigger('updateMetadata', metadata);

        return self;
    }

    function getMetadata() {
        return metadata;
    }

    /*--------------------------------------------------------------------------
     * Curbside Pickup Details
     */
    function setCurbsidePickupDetails(new_curbside_pickup_details) {
        if (_.isEqual(new_curbside_pickup_details, curbside_pickup_details)) {
            return self;
        }
        setCurbsidePickupDetailsBrand(new_curbside_pickup_details.brand);
        setCurbsidePickupDetailsMake(new_curbside_pickup_details.make);
        setCurbsidePickupDetailsColour(new_curbside_pickup_details.colour);
        setCurbsidePickupDetailsPlateNumber(new_curbside_pickup_details.plate_number);

        trigger('updateCurbsidePickupDetails', curbside_pickup_details);

        return self;
    }

    function getCurbsidePickupDetails() {
        return curbside_pickup_details;
    }

    function setCurbsidePickupDetailsBrand(new_curbside_pickup_details_brand) {
        if (
            (!new_curbside_pickup_details_brand &&
                !curbside_pickup_details.brand) ||
            new_curbside_pickup_details_brand === curbside_pickup_details.brand
        ) {
            return self;
        }

        curbside_pickup_details.brand = new_curbside_pickup_details_brand;

        return self;
    }

    function setCurbsidePickupDetailsColour(
        new_curbside_pickup_details_colour
    ) {
        if (
            (!new_curbside_pickup_details_colour &&
                !curbside_pickup_details.colour) ||
            new_curbside_pickup_details_colour ===
                curbside_pickup_details.colour
        ) {
            return self;
        }

        curbside_pickup_details.colour = new_curbside_pickup_details_colour;

        return self;
    }

    function setCurbsidePickupDetailsMake(new_curbside_pickup_details_make) {
        if (
            (!new_curbside_pickup_details_make &&
                !curbside_pickup_details.make) ||
            new_curbside_pickup_details_make === curbside_pickup_details.make
        ) {
            return self;
        }

        curbside_pickup_details.make = new_curbside_pickup_details_make;

        return self;
    }

    function setCurbsidePickupDetailsPlateNumber(
        new_curbside_pickup_details_plate_number
    ) {
        if (
            (!new_curbside_pickup_details_plate_number &&
                !curbside_pickup_details.plate_number) ||
            new_curbside_pickup_details_plate_number ===
                curbside_pickup_details.plate_number
        ) {
            return self;
        }

        curbside_pickup_details.plate_number = new_curbside_pickup_details_plate_number;

        return self;
    }

    function areCurbsideDetailsComplete() {
        return !!(curbside_pickup_details.brand &&
            curbside_pickup_details.make &&
            curbside_pickup_details.colour);
    }

    function isCurbsidePickupOrder() {
        return isCurbsidePickup
    }

    function setIsCurbsidePickupOrder(value) {
        isCurbsidePickup = value;
    }

    /**
     * Kiosk
     */
    function setKioskId(new_kiosk_id) {
        if (kiosk_id === new_kiosk_id) {
            return self;
        }

        kiosk_id = new_kiosk_id;
        trigger('updateKioskId', kiosk_id);

        return self;
    }

    function getKioskId() {
        return kiosk_id;
    }

    /*--------------------------------------------------------------------------
     * Custom HTML
     */
    function setCustomHtml(new_custom_html) {
        if (_.isEqual(custom_html, new_custom_html)) {
            return;
        }

        const old_slots = _.keys(custom_html);
        const slots = _.keys(new_custom_html);
        const slots_to_remove = _.remove(old_slots, function(slot) {
            return !_.includes(slots, slot);
        });

        _.each(slots, function(slot) {
            setCustomHtmlForSlot(slot, new_custom_html[slot]);
        });

        // Remove slots
        _.each(slots_to_remove, function(slot) {
            setCustomHtmlForSlot(slot, null);
        });

        custom_html = new_custom_html;

        return self;
    }

    function setCustomHtmlForSlot(slot, html) {
        custom_html[slot] = html;
        trigger('updateCustomHtmlSlot', slot, html);
        return self;
    }

    function getCustomHtml() {
        return custom_html;
    }

    function getCustomHtmlForSlot(slot) {
        if (slot in custom_html) {
            return custom_html[slot];
        }
        return null;
    }

    /*--------------------------------------------------------------------------
     * Voucher
     */
    function setVoucher(new_voucher) {
        if (new_voucher.code !== undefined) {
            setVoucherCode(new_voucher.code);
        }
        if (new_voucher.voucher_applied !== undefined) {
            setVoucherApplied(new_voucher.voucher_applied);
        }
        if (new_voucher.error_message !== undefined) {
            setVoucherErrorMessage(new_voucher.error_message);
        }
        if (new_voucher.will_unapply_on_confirmation !== undefined) {
            setVoucherWillUnapplyOnConfirmation(
                new_voucher.will_unapply_on_confirmation
            );
        }
    }

    function setVoucherCode(new_voucher_code) {
        if (_.isString(new_voucher_code)) {
            new_voucher_code = new_voucher_code.substr(0, 34);
        }
        if (
            (!new_voucher_code && !voucher.code) ||
            new_voucher_code === voucher.code
        ) {
            return self;
        }

        voucher.code = new_voucher_code || '';
        trigger('updateVoucherCode', voucher.code);

        // Anticipate that voucher and discounts will be wiped
        if (voucher.code === '') {
            setVoucherApplied(null);
            setVoucherErrorMessage(null);
            setDiscountsTotal(0.0, 0.0);
            setProductSuggestions([]);
        }

        return self;
    }

    function setVoucherApplied(new_voucher_applied) {
        if (
            (!new_voucher_applied && !voucher.voucher_applied) ||
            (new_voucher_applied &&
                voucher.voucher_applied &&
                new_voucher_applied.id === voucher.voucher_applied.id)
        ) {
            return self;
        }

        if (!new_voucher_applied) {
            setVoucherWillUnapplyOnConfirmation(false);
        }

        voucher.voucher_applied = new_voucher_applied || null;
        trigger('updateVoucherApplied', voucher.voucher_applied);

        return self;
    }

    function setVoucherErrorMessage(new_error_message) {
        if (
            (!new_error_message && !voucher.error_message) ||
            new_error_message === voucher.error_message
        ) {
            return self;
        }

        voucher.error_message = new_error_message || '';
        trigger('updateVoucherErrorMessage', voucher.error_message);

        return self;
    }

    function setVoucherWillUnapplyOnConfirmation(will_unapply_on_confirmation) {
        if (
            will_unapply_on_confirmation ===
            voucher.will_unapply_on_confirmation
        ) {
            return self;
        }

        voucher.will_unapply_on_confirmation = !!will_unapply_on_confirmation;
        trigger(
            'updateVoucherWillUnapplyOnConfirmation',
            voucher.will_unapply_on_confirmation
        );

        return self;
    }

    /*--------------------------------------------------------------------------
     * Platform
     */
    function getPlatform() {
        return Mobi2Go.app.getPlatform();
    }

    /*--------------------------------------------------------------------------
     * Rewards
     */
    function getAvailableRewards() {
        if (customer) {
            return customer.getRewards();
        }

        return [];
    }

    function updateAvailableRewards() {
        const prev_available_rewards = available_rewards;
        available_rewards = getAvailableRewards();

        if (!_.isEqual(prev_available_rewards, available_rewards)) {
            trigger('updateAvailableRewards', available_rewards);
        }
    }

    function getReward() {
        return reward;
    }

    function setReward(new_reward) {
        if (reward === null && !new_reward) {
            return self;
        }

        if (reward && new_reward && reward.id === new_reward.id) {
            return self;
        }

        let reward_to_set;

        if (new_reward) {
            reward_to_set = _.find(getAvailableRewards(), function(reward) {
                if (new_reward.id) {
                    return reward.id === new_reward.id;
                }
            });
        }

        if (!_.isEqual(reward_to_set, reward)) {
            reward = reward_to_set;
            trigger('updateReward', reward);

            // An order may only have either a reward or voucher, so preimptively
            // remove any voucher code
            if (reward) {
                setVoucherCode('');
            } else {
                setDiscountsTotal(0.0, 0.0);
                setProductSuggestions([]);
            }

            updateAvailableRewards();
        }
    }

    /*--------------------------------------------------------------------------
     * Loyalty
     */
    function getLoyalty(scheme_name) {
        if (scheme_name) {
            return loyalty[scheme_name];
        }

        return loyalty;
    }

    function setLoyalty(new_loyalty) {
        if (_.isArray(new_loyalty)) {
            new_loyalty = {};
        }
        if (_.isEqual(loyalty, new_loyalty)) {
            return;
        }

        loyalty = new_loyalty;
        trigger('updateLoyalty', loyalty);
    }

    function willLoyaltyProgress() {
        return !!(
            loyalty?.PunchCard?.punches_earned ||
            loyalty?.PunchCard?.rewards_earned?.length
        );
    }

    /*--------------------------------------------------------------------------
     * Discounts
     */
    function setDiscounts(new_discounts) {
        if (_.isEqual(discounts, new_discounts)) {
            return;
        }

        discounts = new_discounts;
        trigger('updateDiscounts', discounts);
    }

    function setDiscountsTotal(new_discounts_total) {
        if (discounts_total.isEqual(new_discounts_total)) return;

        discounts_total.set(new_discounts_total);
        trigger('updateDiscountsTotal', discounts_total);
    }

    /*--------------------------------------------------------------------------
     * Tax
     */

    // Deprecated
    function getSalesTax() {
        return location ? location.sales_tax : null;
    }

    // Deprecated
    function getSalesTaxTotal() {
        const total_tax = products.getSalesTaxTotal();

        total_tax.subtract(discounts_total.getTaxAmount());
        total_tax.add(surcharge.getTaxPrice());
        total_tax.add(tip.getTaxPrice());
        total_tax.add(delivery_fee.getTaxPrice());

        if (total_tax.toFloat() < 0) {
            total_tax.set(0);
        }

        return total_tax;
    }

    function setTaxTotals(new_tax_totals) {
        new_tax_totals = _.map(new_tax_totals, function(tax) {
            return {
                name: tax.name,
                total: new Price(tax.total),
            };
        });

        if (_.isEqual(new_tax_totals, tax_totals)) {
            return self;
        }

        tax_totals = new_tax_totals;
        trigger('updateTaxTotals', tax_totals);

        return self;
    }

    /*--------------------------------------------------------------------------
     * Total
     */
    function calculateTotal() {
        // Skip if set() is being executed
        if (is_setting) {
            return;
        }

        const total = new Price(0.0, getSalesTax())
            .add(products.getTotal().toFloat())
            .add(delivery_fee.toFloat())
            .add(tip.toFloat())
            .add(surcharge.toFloat())
            .subtract(discounts_total.toFloat());
        ;

        setTotal(total.toFloat());
    }

    function setTotal(new_total) {
        if (new_total < 0) {
            new_total = 0;
        }

        if (!total.isEqual(new_total)) {
            total.set(new_total);
            trigger('updateTotal', total);
        }
    }

    /**-------------------------------------------------------------------------
     * Dispatcher
     */
    const dispatcher = {
        status: null,
        message: null,
    };
    function setDispatcher(dispatcher) {
        if (dispatcher && dispatcher.status !== undefined)
            setDispatcherStatus(dispatcher.status);
        if (dispatcher && dispatcher.message !== undefined)
            setDispatcherMessage(dispatcher.message);
    }

    function setDispatcherStatus(status) {
        if (status !== dispatcher.status) {
            dispatcher.status = status;
        }
    }

    function setDispatcherMessage(message) {
        if (message !== dispatcher.message) {
            dispatcher.message = message;
        }
    }

    /*--------------------------------------------------------------------------
     * Progress
     */
    var progress = {
        phase: 'unconfirmed',
        status: 'unconfirmable',
        message: 'location required',
        errors: [],
        is_confirmable: null,
    };

    function setProgress(new_progress) {
        const currentPhase = progress.phase;
        const currentStatus = progress.status;

        if (new_progress.phase !== undefined) {
            setProgressPhase(new_progress.phase);
        }
        if (new_progress.status !== undefined) {
            setProgressStatus(new_progress.status);
        }
        if (new_progress.message !== undefined) {
            setProgressMessage(new_progress.message);
        }
        if (new_progress.errors !== undefined) {
            setProgressErrors(new_progress.errors);
        }
        if (new_progress.confirmability_status !== undefined) {
            setProgressConfirmabilityStatus(new_progress.confirmability_status);
        }

        if (
            (currentPhase !== progress.phase ||
                currentStatus !== progress.status) &&
            progress.phase === 'unconfirmed' &&
            progress.status === 'confirmable'
        ) {
            trigger('updateConfirmabilityStatus');
        }
    }

    /**-------------------------------------------------------------------------
     * Progress Phase
     */
    function getProgressPhase() {
        return progress.phase;
    }

    function setProgressPhase(new_phase) {
        if (new_phase === progress.phase) {
            return self;
        }

        progress.phase = new_phase;
        trigger('updateProgressPhase', progress.phase);

        // Refresh Customer as they may have earned rewards or loyalty progress
        // after confirmation
        if (progress.phase === 'confirmed' && customer) {
            customer.refresh();
        }

        if (progress.phase === 'abandoned') {
            once('syncSuccess', function() {
                set({
                    id: null,
                    progress: {
                        phase: 'unconfirmed',
                    },
                });
            });
        }

        return self;
    }

    function shouldShowPreconfirmationWarning() {
        const dataCandyisEnabled = Mobi2Go.Customer.getLoyalty(
            'DataCandyLoyalty'
        );
        return (voucher.will_unapply_on_confirmation ||
            (isUpsellPreconfirmationEnabled && getProductSuggestions().length > 0 && !dataCandyisEnabled)
        );

    }

    function isConfirmable() {
        return confirmability_status.isConfirmable();
    }

    function confirm() {
        if (!isConfirmable()) {
            throw new Error(`Cannot confirm order, ${getProgressMessage()}`);
        }
        setProgressPhase('confirming');
        resetProgressMessage(); // Kinda crappy...

        return self;
    }

    function cancelConfirmation() {
        if (progress.phase === 'confirming') {
            return setProgressPhase('unconfirmed');
        }

        return self;
    }

    /**-------------------------------------------------------------------------
     * Progress Status
     */
    function getProgressConfirmabilityStatus() {
        return confirmability_status;
    }

    function setProgressConfirmabilityStatus(new_status) {
        confirmability_status.set(new_status);
    }

    function getProgressStatus() {
        return progress.status;
    }

    function setProgressStatus(new_status) {
        if (new_status === progress.status) {
            return self;
        }

        progress.status = new_status;
        trigger('updateProgressStatus', progress.status);

        return self;
    }

    function getUnconfirmedProgressMessage() {
        if (!location) {
            return 'location required';
        }

        if (!customer) {
            return 'customer required';
        }

        if (!method) {
            return 'method required';
        }

        if (!expected_at) {
            return 'expected_at required';
        }

        if (method === 'delivery' && !address) {
            return 'address required';
        }

        if (!products.getQuantity()) {
            return 'product required';
        }

        if (method === 'delivery' && !isMeetingDeliveryMinimumProductsTotal()) {
            return 'delivery minimum not met';
        }

        if (!payment.method) {
            return 'payment method required';
        }

        return 'ready to confirm';
    }

    function updateProgressStatus() {
        if (progress.phase === 'unconfirmed') {
            setProgressStatus(
                isConfirmable() ? 'confirmable' : 'unconfirmable'
            );
            setProgressMessage(getUnconfirmedProgressMessage());
        }
    }

    /**-------------------------------------------------------------------------
     * Progress Message
     */
    function getProgressMessage() {
        return progress.message;
    }
    function setProgressMessage(new_message) {
        const is_updated = new_message !== progress.message;
        progress.message = new_message;

        if (is_updated) {
            trigger('updateProgressMessage', progress.message);
        }

        return self;
    }

    function resetProgressMessage() {
        setProgressMessage('');
    }

    /**-------------------------------------------------------------------------
     * Progress error
     */
    function getProgressError() {
        if (progress.errors.length) {
            return progress.errors[progress.errors.length - 1].message;
        }

        return null;
    }

    function setProgressErrors(errors) {
        if (!_.isEqual(errors, progress.errors)) {
            progress.errors = errors;

            if (errors.length) {
                const last_error = errors[errors.length - 1];
                trigger('updateProgressError', last_error);
            }
        }
    }

    function getAvailablePostConfirmationStatuses() {
        return available_post_confirmation_statuses;
    }

    /*--------------------------------------------------------------------------
     * Backend communication
     */
    function save(callback) {
        if (!is_ready) {
            return self;
        }

        if (_.isFunction(callback)) {
            once('syncSuccess', callback);
        }

        serverRequest('POST');

        return self;
    }

    /*--------------------------------------------------------------------------
     * Used for non-essential order updates where skipping a save to the server
     * is possible. An optimisation intended to reduce API call volume.
     */
    function saveConservatively() {
        const feature_flag = Mobi2Go.app.feature_flags?.conservative_load;
        const can_safely_avoid_save =
            !isConfirmable() && discounts.length === 0 && !voucher.code;

        if (feature_flag && can_safely_avoid_save) {
            return;
        }

        save();
    }

    function load(callback) {
        if (!id) {
            return self;
        }

        if (_.isFunction(callback)) {
            once('syncSuccess', callback);
        }

        serverRequest('GET');

        return self;
    }

    let request_type_waiting_for_debounce;
    let pending_update_requests_count = 0;
    let pending_get_requests_count = 0;
    let is_updating = false;
    let is_loading = false;
    const isUpdating = () => is_updating;
    const isLoading = () => is_loading;
    const isSyncing = () => isUpdating() || isLoading();
    const updateSyncingStates = () => {
        const new_is_updating =
            request_type_waiting_for_debounce === 'PUT' ||
            pending_update_requests_count > 0;
        const new_is_loading =
            request_type_waiting_for_debounce === 'GET' ||
            pending_get_requests_count > 0;
        const previous_is_syncing = isSyncing();
        if (new_is_updating !== is_updating) {
            is_updating = new_is_updating;
            trigger('updateIsUpdating', is_updating);
        }
        if (new_is_loading !== is_loading) {
            is_loading = new_is_loading;
            trigger('updateIsLoading', is_loading);
        }
        const is_syncing = isSyncing();
        if (previous_is_syncing !== is_syncing) {
            trigger('updateIsSyncing', is_syncing);
        }
    };
    const serverRequest = (() => {
        let request_ticker = 0;
        const request_debounce_delay = 750;

        let last_request_payload = {};

        const dispatchServerRequest = (request_id, endpoint, type, payload) => {
            request_type_waiting_for_debounce = undefined;

            if (type === 'GET') {
                pending_get_requests_count += 1;
            } else {
                pending_update_requests_count += 1;
            }
            updateSyncingStates();
            trigger('syncRequest', payload);

            const markRequestAsComplete = () => {
                if (type === 'GET') {
                    pending_get_requests_count -= 1;
                } else {
                    pending_update_requests_count -= 1;
                }
                updateSyncingStates();
            };

            if (
                time_slots.isDirty() &&
                getPlatform() !== PLATFORMS.PAYATTABLE
            ) {
                endpoint[endpoint.length - 1] += '?include_time_slots';
            }

            Mobi2Go.app.serverRequest({
                api_version: 1,
                endpoint,
                type,
                data: payload,
                success(data) {
                    markRequestAsComplete();

                    if (request_id !== request_ticker) {
                        trigger('syncAbort', 'request superceded');
                        return;
                    }

                    set(data);
                    trigger('syncSuccess', data);
                },
                error(error) {
                    markRequestAsComplete();
                    return handleServerError(error);
                },
            });
        };

        const serverRequestDebounced = _.debounce(
            dispatchServerRequest,
            request_debounce_delay
        );

        /**
         * This handles requests to load, create and save the order.
         *
         * Requests are debounced to reduce the amount of server calls.
         *
         * They are safe guards to prevent race conditions.
         *
         * 1. Any call, load or save, cannot override another more recent call.
         * 2. A load call cannot be queued if a save call is pending.
         */
        return type => {
            const is_get_request_pending =
                request_type_waiting_for_debounce === 'GET' ||
                pending_get_requests_count > 0;
            const is_update_request_pending =
                (request_type_waiting_for_debounce !== undefined &&
                    request_type_waiting_for_debounce !== 'GET') ||
                pending_update_requests_count > 0;

            const is_server_request_pending =
                is_get_request_pending || is_update_request_pending;

            const pending_request_has_unconfirmed_progress_phase =
                is_update_request_pending &&
                last_request_payload &&
                last_request_payload.progress &&
                last_request_payload.progress.phase &&
                last_request_payload.progress.phase === 'unconfirmed';

            // Never overlap requests when order is confirming and the
            // currently requesting payload is not unconfirmed
            if (
                getProgressPhase() === 'confirming' &&
                is_server_request_pending &&
                !pending_request_has_unconfirmed_progress_phase
            ) {
                return;
            }

            type = type || 'GET';

            if (type === 'GET' && is_update_request_pending) {
                // A GET request cannot be made if an update request is pending
                // because a GET request doesn't lock and therefore we cannot
                // guarantee the data will be in sync with the pending
                // update request.
                return;
            }

            request_ticker += 1;
            const request_id = request_ticker;
            const endpoint = ['orders'];
            const payload = type === 'GET' ? {} : toApiJson();

            if (id) {
                endpoint.push(id);
                if (type === 'POST') {
                    type = 'PUT';
                }
            }

            request_type_waiting_for_debounce = type;
            last_request_payload = payload;
            serverRequestDebounced(request_id, endpoint, type, payload);
            updateSyncingStates();
        };
    })();

    function toApiJson() {
        let customer_data = null;
        if (customer) {
            customer_data = customer.get();

            if (
                customer.isPasswordProvided() &&
                progress.phase === 'confirming' &&
                payment.card_token_id
            ) {
                customer_data.password = customer.getPassword();
            }
        }

        return {
            is_test,
            customer: customer_data,
            address: address ? address.get() : null,
            location: location
                ? { id: location.id, name: location.name }
                : null,
            menu: menu ? { id: menu.id, name: menu.name } : null,
            method,
            expected_at: expected_at ? expected_at.toISOString(true) : null,
            products: products.toJson(),
            tip: tip.toFloat(),
            credit: credit.toFloat(),
            payment: {
                method: payment.method,
                processor: payment.processor
                    ? { name: payment.processor.name }
                    : null,
                card_token_id: payment.card_token_id,
                card_token_saving_enabled: payment.card_token_saving_enabled,
            },
            comment,
            reward: reward ? { id: reward.id } : null,
            voucher: { code: voucher.code },
            progress: { phase: progress.phase },
            latitude,
            longitude,
            platform: getPlatform(),
            storefront_url: window.location.href,
            storefront_version: Mobi2Go.app.VERSION,
            metadata,
            container_app: Mobi2Go.app.getContainerApp(),
            language,
            kiosk_id,
            table_id,
            collect_id,
            curbside_pickup_details,
        };
    }

    function handleServerError(error) {
        // Recover if session loses authorisation to current order
        if (error.status === 401) {
            setId(null);

            if (getProgressPhase() === 'confirmed') {
                finish();
            }

            return true;
        }

        return false;
    }

    /*--------------------------------------------------------------------------
     * Reset - clear existing order
     */
    function reset(preserve_id) {
        is_resetting = true;
        trigger('resetting');
        set({
            id: preserve_id ? id : null,
            location: null,
            method: null,
            wait_time: null,
            expected_at: null,
            earliest_expected_at: null,
            products: [],
            comment: '',
            voucher: {
                code: '',
                voucher_applied: null,
                error_message: '',
            },
            loyalty: {},
            reward: null,
            delivery_fee: 0.0,
            delivery_fee_discount: 0.0,
            discounts: [],
            discounts_total: 0.0,
            product_suggestions: [],
            payment: {
                method: null,
                status: 'method required',
                card_token_id: null,
                card_token_saving_enabled: false,
                available_processors: [],
                processor: null,
                processor_transaction: {
                    status: 'unconfirmed',
                    message: 'transaction uninitated',
                    is_card_tokenisation_available: false,
                },
            },
            progress: {
                phase: 'unconfirmed',
                status: 'unconfirmable',
                message: 'location required',
                is_confirmable: null,
                confirmability_status: {
                    is_confirmable: false,
                    error_id: null,
                    error_title: null,
                    error_message: null,
                },
            },
            surcharge: 0.0,
            tip: 0.0,
            credit: 0.0,
            rebates: [],
            total: 0.0,
            custom_html: {},
            metadata: {},
            kiosk_id: null,
            table_id: null,
            collect_id: null,
            curbside_pickup_details: {
                colour: '',
                make: '',
                brand: '',
                plate_number: '',
            },
        });

        is_resetting = false;
        trigger('reset');

        return self;
    }

    /*--------------------------------------------------------------------------
     */
    function abandon(callback) {
        set({
            progress: {
                phase: 'abandoned',
            },
        });

        save(function() {
            set({
                id: null,
                progress: {
                    phase: 'unconfirmed',
                },
            });
            if (_.isFunction(callback)) {
                callback();
            }
        });
    }

    /*--------------------------------------------------------------------------
     * Finish - Soft Reset to use after an order has been confirmed
     */
    function finish() {
        const preserve_location = location;
        const preserve_method = method;
        const preserve_address = address;
        const preserve_table_id = table_id;

        reset();

        set({
            location: preserve_location,
            method: preserve_method,
        });

        if (isTablePlatform()) {
            set({ table_id: preserve_table_id });
        }

        if (preserve_address && preserve_method === 'delivery') {
            setAddress(preserve_address);
        }
    }

    /**-------------------------------------------------------------------------
     * It's possible that client state may become out of sync with server state
     * if for example there are two browsers open with the same order. Because
     * client side is the 'truth' each browser may continually overwrite the
     * other's state. To prevent this, we can call the load method at an interval
     */
    function startLoadInterval() {
        clearTimeout(startLoadInterval.interval);
        startLoadInterval.interval = setInterval(load, 20000);
    }

    /**-------------------------------------------------------------------------
     * @param  level
     * @param {type} message
     * @param {type} context
     */
    function log(message, context, level) {
        if (id) {
            trigger('eventLogged', message, context);
            Mobi2Go.app.serverRequest({
                api_version: 1,
                endpoint: ['orders', id, 'log'],
                type: 'POST',
                data: {
                    level: level || 200,
                    message,
                    context: _.extend({ client_side_event: true }, context),
                },
                error() {
                    return true;
                },
            });
        }
    }

    function verifyPosOrder() {
        if (id) {
            return new Promise((resolve, reject) => {
                Mobi2Go.app.serverRequest({
                    api_version: 1,
                    endpoint: ['orders', id, 'verify_pos_order'],
                    type: 'POST',
                    success() {
                        resolve(true);
                        return true;
                    },
                    error(e) {
                        if (
                            e.responseJSON &&
                            e.responseJSON.error &&
                            e.responseJSON.error ===
                                PAY_AT_TABLE_ORDER_UPDATE_CODE
                        ) {
                            reject(e);
                            return true;
                        }
                    },
                });
            });
        }

        return false;
    }

    /**-------------------------------------------------------------------------
     * Event bindings
     */
    bind({
        updateCustomer() {
            updateAvailableAddresses();
            updateAvailableRewards();
            automaticallyAdjustCredit();
        },
        updateLocation() {
            updateAvailableMenus();
            updateAvailableMethods();

            if (server_time_slots) {
                available_dates = [];
                fetchTimeSlots();
            } else {
                updateAvailableDates();
                updateAvailableTimes();
            }

            updateAvailableDeliveryAreas();
            updateAvailableAddresses();
            updateAvailablePaymentMethods();
            updateMenuRestrictions();
        },
        updateMethod() {
            updateAvailableAddresses();

            if (server_time_slots) {
                available_dates = [];
                fetchTimeSlots();
            } else {
                updateAvailableDates();
                updateAvailableTimes();
            }

            updateAvailableDeliveryAreas();
            updateAvailablePaymentMethods();
        },
        updateTimeSlots() {
            updateAvailableDates();
            updateAvailableTimes();
            ensureExpectedAtIsAvailable();
        },
        updateTimeSlotsIsSyncing(is_syncing) {
            if (!is_syncing) {
                updateAvailableDates();
                updateAvailableTimes();
            }
        },
        updateMenu: switchOrRemoveProductsNotInLocationMenu,
        updateMenuRestrictions: removeRestrictedProducts,
        addProduct: updateProductSuggestionsOnProductAdd,
        updateProductsQuantity() {
            removeRestrictedProducts();
            switchOrRemoveProductsNotInLocationMenu(); // Should'nt be necess
        },
        updateWaitTime() {
            if (location) {
                location.clearAvailableOrderDatesAndTimesCache();
            }
            if (server_time_slots) {
                fetchTimeSlots();
            } else {
                updateAvailableDates();
                updateAvailableTimes();
            }
        },
        updateEarliestExpectedAt() {
            updateAvailableDates();
            updateAvailableTimes();
        },
        updateExpectedAt() {
            updateAvailableTimes();
            updateExpectedInString();
            updateMinutesToClosing();
        },
        updatePaymentMethod() {
            updatePaymentOption();
            automaticallyAdjustCredit();
        },
        updatePaymentProcessor: updatePaymentOption,
        updatePaymentCardToken: updatePaymentOption,
        updateAvailablePaymentProcessors: updateAvailablePaymentOptions,
        updateAvailablePaymentMethods: updateAvailablePaymentOptions,
        updateAvailablePaymentCardTokens: updateAvailablePaymentOptions,
        updateTotal() {
            automaticallyAdjustCredit();
            updatePaymentTotal();
        },
        updateCredit: updatePaymentTotal,
    });

    // Total calculation
    bind({
        updateProductsTotal: calculateTotal,
        updateDeliveryFee: calculateTotal,
        updateTip: calculateTotal,
        updateDiscountsTotal: calculateTotal,
    });

    // Progress status
    bind({
        updateCustomer: updateProgressStatus,
        updateLocation: updateProgressStatus,
        updateAddress: updateProgressStatus,
        updateMethod: updateProgressStatus,
        updateProductsQuantity: updateProgressStatus,
        updateProductsTotal: updateProgressStatus,
        updatePaymentMethod: updateProgressStatus,
    });

    // Auto saving
    bind({
        updateCustomer: save,
        updateAddress: save,
        updateLocation: save,
        updateMenu: save,
        updateMethod: saveConservatively,
        updateExpectedAt: save,
        addProduct: saveConservatively,
        removeProduct: saveConservatively,
        updateProductModifierSelection: saveConservatively,
        updateProductModifierQuantity: saveConservatively,
        updateProductMetadata: saveConservatively,
        updateReward: save,
        updateVoucherCode: save,
        updatePaymentMethod: save,
        updatePaymentProcessor: save,
        updatePaymentCardToken: save,
        updatePaymentCardTokenSavingEnabled: save,
        updateTotal: saveConservatively,
        updateProgressPhase: save,
        updateCredit: save,
        updateRebates: save,
        updateComment: save,
        updateLanguage: save,
        updateKioskId: save,
        updateTableId: save,
        updateMetadata: save,
        updateIsTest: save,
        updateCurbsidePickupDetails: save,
    });

    return (self = {
        get,
        set,
        apply,
        setReady() {
            is_ready = true;
            trigger('ready');
        },
        isReady() {
            return is_ready;
        },
        onReady(callback) {
            if (is_ready) {
                _.isFunction(callback) && _.defer(callback);
            } else {
                once('ready', callback);
            }
            return self;
        },

        getId() {
            return id;
        },

        isTest,
        setIsTest,

        getCustomer() {
            return customer;
        },
        setCustomer,
        sendReceiptToEmailAddress,

        getLocation() {
            return location;
        },
        getGroupTabsVersion() {
            if (Customer && Customer.getCurrentGroupTab()) {
                return Customer.getCurrentGroupTab().version;
            }
            if (app.getEnableGroupTabs()) {
                return app.getEnableGroupTabs();
            }
            if (app.getEnableGroupTabs() === 0) {
                return 0;
            }
            if (location) {
                return location.group_tabs_version_id;
            }
        },
        setLocation,
        setLocationById,
        setLocationByName,
        getSalesTax, // Deprecated

        getCollectId() {
            return collect_id;
        },

        setMenu,
        getMenu,
        getMenuTierId,
        getMenuLevelId,
        getAvailableMenus,
        updateAvailableMenus,

        getAddress() {
            return address;
        },
        setAddress,
        setAddressAfterCustomerSet,
        setAddressById,
        getAvailableAddresses,
        getAvailableDeliveryAreas,
        getWaitTime() {
            return wait_time;
        },
        getMethod() {
            return method;
        },
        setMethod,
        getMethodLabels() {
            return method_labels;
        },
        setMethodLabels(labels) {
            return _.merge(method_labels, labels);
        },
        getMethodLabel(method) {
            return method_labels[method];
        },
        getAvailableMethods,
        getDeliveryFee() {
            return delivery_fee;
        },
        getDeliveryFeeDiscount() {
            return delivery_fee_discount;
        },
        getDeliveryMinimumTotal,
        isMeetingDeliveryMinimumProductsTotal,

        getTableId,
        setTableId,
        setTableOrderingEnabled(is_enabled) {
            is_table_ordering_enabled = !!is_enabled;
        },
        isTableOrderingEnabled() {
            return is_table_ordering_enabled;
        },
        getExpectedAt() {
            return expected_at;
        },
        setExpectedAt,
        getEarliestExpectedAt() {
            return earliest_expected_at;
        },
        getAvailableDates,
        getAvailableTimes,
        getAsapExpectedTime,
        isScheduled,
        isExpectedAsap,
        isExpectedToday,
        isExpectedTomorrow,
        getExpectedInString,
        getMinutesToExpectedAt,
        getMinutesToClosing,
        isSyncingTimeSlots,
        invalidateTimeSlots,

        isUpdating,
        isLoading,
        isSyncing,

        getProducts() {
            return products.get();
        },
        addProduct,
        removeProduct,
        containsProduct,
        isProductRestricted,
        isProductSoldOut,
        isProductHidden,
        isModifierGroupRestricted,
        isModifierRestricted,
        isModifierSoldOut,
        isModifierHidden,
        getUnavailableProductsForMenu,
        getMenuRestrictions,
        getProductsTotal,
        getProductsQuantity,
        getFirstProductWithUnmetModifierRequirements,
        getRestrictedProductsForLocation,
        getRestrictedModifiersForLocationAndProduct,
        getUnavailableAndRestrictedProductsForLocation,
        getProductSuggestions,
        overrideProductSuggestions,
        disableProductSuggestionsOverride,

        getAvailablePaymentOptions,
        setPaymentOption,
        getPaymentOption,

        getPayment() {
            return payment;
        },
        getPaymentMethod() {
            return payment.method;
        },
        setPaymentMethod,
        getAvailablePaymentMethods,
        isPaymentMethodAvailable,
        getPaymentProcessor() {
            return payment.processor;
        },
        getPaymentProcessorInstanceByName,
        setPaymentProcessor,
        setAvailablePaymentProcessors,
        getAvailablePaymentProcessors,
        getAvailablePaymentProcessorInstances,

        isPasswordRequiredForPaymentToken,
        isPasswordProvided,
        providePassword,
        provideFacebookAuthentication,
        provideAppleAuthentication,
        provideGoogleAuthentication,
        shouldDisplayTaxExclusivePrices() {
            if (!location) {
                return false;
            }

            return location.has_tax_exclusive_prices;
        },

        isPaymentCardTokenisationAvailable,
        getPaymentCardTokenSavingEnabled,
        setPaymentCardTokenSavingEnabled,
        getAvailablePaymentCardTokens,
        getPaymentCardToken,
        setPaymentCardToken,
        setPaymentCardTokenById,

        getVoucher() {
            return voucher;
        },
        getVoucherCode() {
            return voucher.code;
        },
        setVoucherCode,
        getVoucherApplied() {
            return voucher.voucher_applied;
        },
        getVoucherErrorMessage() {
            return voucher.error_message;
        },
        getVoucherWillUnapplyOnConfirmation() {
            return voucher.will_unapply_on_confirmation;
        },

        getLoyalty,
        willLoyaltyProgress,

        getAvailableRewards,
        getReward,
        setReward,

        getComment() {
            return comment;
        },
        setComment,

        getMetadata,
        setMetadata,

        setCurbsidePickupDetails,
        getCurbsidePickupDetails,
        getCurbsidePickupDetailsBrand() {
            return curbside_pickup_details.brand;
        },
        getCurbsidePickupDetailsColour() {
            return curbside_pickup_details.colour;
        },
        getCurbsidePickupDetailsMake() {
            return curbside_pickup_details.make;
        },
        getCurbsidePickupDetailsPlateNumber() {
            return curbside_pickup_details.plate_number;
        },
        areCurbsideDetailsComplete,
        isCurbsidePickupOrder,
        setIsCurbsidePickupOrder,

        setKioskId,
        getKioskId,

        getCustomHtmlForSlot,
        getCustomHtml,
        setCustomHtmlForSlot,
        setCustomHtml,

        getDiscounts() {
            return discounts;
        },
        getDiscountsTotal() {
            return discounts_total;
        },

        getSurcharge() {
            return surcharge;
        },
        getTip() {
            return tip;
        },
        setTip,
        getDefaultTip,
        shouldPromptForTip,
        shouldPromptForGifting,
        shouldSkipConfirmationAndPaymentPage,
        getSuggestedTipPercentages() {
            return [10, 15, 20, 25];
        },

        getCredit() {
            return credit;
        },

        getRebates() {
            return rebates;
        },

        getRebatesAppliedTotal() {
            return rebates_applied_total;
        },

        claimRebate,
        cancelRebate,

        applyAvailableCredit,
        unapplyCredit,
        getTaxTotals() {
            return tax_totals;
        },
        getSalesTaxTotal,
        getTotal() {
            return total;
        },
        getTotalWithoutTip,
        getPaymentTotal() {
            return payment.total;
        },

        getPlatform,
        getProgressPhase,
        getProgressStatus,
        getProgressMessage,
        resetProgressMessage,
        getProgressError,
        shouldShowPreconfirmationWarning,
        isConfirmable,
        getProgressConfirmabilityStatus,
        confirm,
        cancelConfirmation,
        getConfirmationCancellationCooldownDuration() {
            return 3700;
        },
        getCommentMaxLength() {
            return 50;
        },

        setAvailablePostConfirmationStatuses,
        getAvailablePostConfirmationStatuses,
        setCurrentPostConfirmationStatus,
        getCurrentPostConfirmationStatus,

        abandon,
        finish,
        log,

        save,
        load,

        isResetting() {
            return is_resetting;
        },
        reset,
        bind,
        trigger,
        unbind,
        once,

        /**---------------------------------------------------------------------
         * Set a custom function which can determine which menu to set.
         * This can be used to do time restricted menus
         */
        setCustomMenuSelector(fn) {
            if (!_.isFunction(fn)) {
                throw new TypeError(
                    'setCustomerMenuSelector must be provided with a function'
                );
            }
            customMenuSelector = fn;
        },

        /**---------------------------------------------------------------------
         * Set a custom function which can filter available times.
         * This can be used to do time restricted menus
         */
        setCustomAvailableTimesFilter(fn) {
            if (!_.isFunction(fn)) {
                throw new TypeError(
                    'setCustomAvailableTimesFilter must be provided with a function'
                );
            }
            customAvailableTimesFilter = fn;
        },

        setCustomAvailableDatesFilter(fn) {
            if (!_.isFunction(fn)) {
                throw new TypeError(
                    'setCustomAvailableDatesFilter must be provided with a function'
                );
            }
            customAvailableDatesFilter = fn;
        },

        /**---------------------------------------------------------------------
         */
        setCustomProductLogic(product_identifier, customFn) {
            bind('orderProductInitiated', function(product) {
                if (
                    (_.isFunction(product_identifier) &&
                        product_identifier(product.menu_product)) ||
                    product.menu_product.name.toLowerCase() ===
                        product_identifier.toLowerCase() ||
                    product.menu_product.backend_name.toLowerCase() ===
                        product_identifier.toLowerCase() ||
                    product.menu_product.id === product_identifier
                ) {
                    product.setCustomLogic(customFn);
                }
            });
        },

        createInstance(id) {
            return Order(id);
        },

        verifyPosOrder,
    });
};
